import React, { useRef, useState } from 'react';
import { useApp } from '../context/AppContext';
import WelcomeMessage from '../components/WelcomeMessage';
import LocationScheduleEditor from '../components/LocationScheduleEditor';
import ActionIconButton from '../components/ActionIconButton';
import { API_BASE_URL } from '../config/api';
import { CHATBOT_WIDGET_API_HOST } from '../utils/chatbotSettings';
import { appendAircraftWebsiteFields, buildAircraftWidgetSnippet, cacheOrganizationWidgetKey, getCachedOrganizationWidgetKey, parseAircraftWebsiteList, validateAircraftWebsiteLists } from '../utils/aircraftWidget';

const emptyAircraftFormData = () => ({ registration: '', model: '', manufacturer: '', year: '', sortOrder: '', hourlyRate: '', ownerRate: '', engineRate: '', status: 'Available', equipment: '', aircraftClass: '', serialNumber: '', ifrEquipped: '', color: '', type: '', showOnWebsite: false, websiteTitle: '', priceLabel: '', websiteDescription: '', avionics: '', features: '', rateDescription: '' });
const locationAirportCode = location => location?.airportCode || location?.airport || '';

const Aircraft = () => {
  const { aircraft, ownedAircraft, addAircraft, updateAircraft, deleteAircraft, fetchAircraft, currentUser, people, locations = [] } = useApp();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dialogPos, setDialogPos] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteAircraftData, setDeleteAircraftData] = useState(null);
  const [message, setMessage] = useState('');
  const [websiteError, setWebsiteError] = useState('');
  const [isSavingAircraft, setIsSavingAircraft] = useState(false);
  const [aircraftWidgetKey, setAircraftWidgetKey] = useState('');
  const [aircraftWidgetLoading, setAircraftWidgetLoading] = useState(false);
  const [aircraftWidgetError, setAircraftWidgetError] = useState(null);
  const [aircraftWidgetRetry, setAircraftWidgetRetry] = useState(0);
  const [widgetScope, setWidgetScope] = useState('all');
  const [widgetLocationCode, setWidgetLocationCode] = useState(() => locationAirportCode(locations.find(location => locationAirportCode(location))) || '');
  const [widgetStyleOverrides, setWidgetStyleOverrides] = useState({ headingFont: '', headingColor: '', textFont: '', textColor: '', accentColor: '', bandColor: '', currency: '' });
  const aircraftWidgetSnippetRef = useRef(null);
  const [widgetCopyMessage, setWidgetCopyMessage] = useState('');
  const [activeTab, setActiveTab] = useState('aircraft');
  const [aircraftImages, setAircraftImages] = useState([]);
  const [aircraftImagesChanged, setAircraftImagesChanged] = useState(false);
  const [aircraftImageModalIndex, setAircraftImageModalIndex] = useState(null);
  const [aircraftLog, setAircraftLog] = useState([]);
  const [logPage, setLogPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [loadingLog, setLoadingLog] = useState(false);
  const [editingLogId, setEditingLogId] = useState(null);
  const [editingLogData, setEditingLogData] = useState({});
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newLogData, setNewLogData] = useState({});
  const [logMessage, setLogMessage] = useState('');
  const [latestTachByAircraft, setLatestTachByAircraft] = useState({});
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [loadingMaintenance, setLoadingMaintenance] = useState(false);
  const [maintenanceStartDate, setMaintenanceStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 12);
    return date.toISOString().split('T')[0];
  });
  const [maintenanceEndDate, setMaintenanceEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [isAddingMaintenance, setIsAddingMaintenance] = useState(false);
  const [newMaintenanceData, setNewMaintenanceData] = useState({});
  const [editingMaintenanceId, setEditingMaintenanceId] = useState(null);
  const [editingMaintenanceData, setEditingMaintenanceData] = useState({});
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [showDeleteMaintenanceModal, setShowDeleteMaintenanceModal] = useState(false);
  const [deleteMaintenanceRecord, setDeleteMaintenanceRecord] = useState(null);
  const [aircraftExpenses, setAircraftExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [aircraftReservations, setAircraftReservations] = useState([]);
  const [loadingReservations, setLoadingReservations] = useState(false);
  const [reservationsStartDate, setReservationsStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [reservationsEndDate, setReservationsEndDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 6);
    return date.toISOString().split('T')[0];
  });
  const [expensesStartDate, setExpensesStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    return date.toISOString().split('T')[0];
  });
  const [expensesEndDate, setExpensesEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceiptImage, setSelectedReceiptImage] = useState('');
  const [selectedReceiptType, setSelectedReceiptType] = useState('');
  const [receiptModalMessage, setReceiptModalMessage] = useState('');
  const [isLoadingReceipt, setIsLoadingReceipt] = useState(false);
  const [ownerStatements, setOwnerStatements] = useState([]);
  const [editingOwnerStatementId, setEditingOwnerStatementId] = useState(null);
  const [ownerStatementMessage, setOwnerStatementMessage] = useState('');
  const [showOwnerStatementDetailsDialog, setShowOwnerStatementDetailsDialog] = useState(false);
  const [selectedOwnerStatement, setSelectedOwnerStatement] = useState(null);
  const [ownerStatementDetailsActiveTab, setOwnerStatementDetailsActiveTab] = useState('aircraftLog');
  const [ownerStatementAircraftLog, setOwnerStatementAircraftLog] = useState([]);
  const [ownerStatementMaintenance, setOwnerStatementMaintenance] = useState([]);
  const [ownerStatementOwnerExpenses, setOwnerStatementOwnerExpenses] = useState([]);
  const [ownerExpenses, setOwnerExpenses] = useState([]);
  const [loadingOwnerExpenses, setLoadingOwnerExpenses] = useState(false);
  const [ownerExpenseMessage, setOwnerExpenseMessage] = useState('');
  const [isAddingOwnerExpense, setIsAddingOwnerExpense] = useState(false);
  const [newOwnerExpenseData, setNewOwnerExpenseData] = useState({});
  const [editingOwnerExpenseId, setEditingOwnerExpenseId] = useState(null);
  const [editingOwnerExpenseData, setEditingOwnerExpenseData] = useState({});
  const [showDeleteOwnerExpenseModal, setShowDeleteOwnerExpenseModal] = useState(false);
  const [deleteOwnerExpenseRecord, setDeleteOwnerExpenseRecord] = useState(null);
  const [showOwnerStatementPdfModal, setShowOwnerStatementPdfModal] = useState(false);
  const [ownerStatementPdfUrl, setOwnerStatementPdfUrl] = useState('');
  const [blockouts, setBlockouts] = useState([]);
  const [loadingBlockouts, setLoadingBlockouts] = useState(false);
  const [blockoutMessage, setBlockoutMessage] = useState('');
  const [isAddingBlockout, setIsAddingBlockout] = useState(false);
  const [newBlockoutData, setNewBlockoutData] = useState({ startDatetime: '', endDatetime: '', reason: '' });
  const [editingBlockoutId, setEditingBlockoutId] = useState(null);
  const [editingBlockoutData, setEditingBlockoutData] = useState({});
  const [squawks, setSquawks] = useState([]);
  const [loadingSquawks, setLoadingSquawks] = useState(false);
  const [squawkMessage, setSquawkMessage] = useState('');
  const [isAddingSquawk, setIsAddingSquawk] = useState(false);
  const [newSquawkData, setNewSquawkData] = useState({ reportedDatetime: '', fixedDate: '', status: 'New', issue: '', details: '' });
  const [editingSquawkId, setEditingSquawkId] = useState(null);
  const [editingSquawkData, setEditingSquawkData] = useState({});
  const squawkStatusOptions = ['New', 'Assessed', 'In-Progress', 'Deferred', 'Duplicate', 'Non Issue', 'Fixed', 'Complete'];

  const toLocalDatetimeInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const toIsoDatetime = (value) => {
    if (!value) return undefined;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return date.toISOString();
  };

  const formatDateMmDdYyyy = (value) => {
    if (!value) return '';
    const source = String(value).trim();
    const datePartMatch = source.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (datePartMatch) {
      return `${datePartMatch[2]}/${datePartMatch[3]}/${datePartMatch[1]}`;
    }

    const date = new Date(source);
    if (Number.isNaN(date.getTime())) {
      return source;
    }

    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}`;
  };

  const formatReservationTime = (value) => {
    const numericTime = Number(value);
    if (!Number.isFinite(numericTime)) return '';
    const hours = Math.floor(numericTime / 100);
    const minutes = numericTime % 100;
    const hour12 = hours % 12 || 12;
    return `${hour12}:${String(minutes).padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`;
  };

  const toLocalDatetimeDisplay = (value) => {
    if (!value) return '';
    const source = String(value).trim();
    const pad = (n) => String(n).padStart(2, '0');
    const monthMap = {
      Jan: 1,
      Feb: 2,
      Mar: 3,
      Apr: 4,
      May: 5,
      Jun: 6,
      Jul: 7,
      Aug: 8,
      Sep: 9,
      Oct: 10,
      Nov: 11,
      Dec: 12
    };

    // Parse core date/time directly from backend string and apply fixed -4 hour shift.
    const match = source.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);
      const hour = Number(match[4]);
      const minute = Number(match[5]);
      const second = Number(match[6] || '0');

      const shiftedUtcMs = Date.UTC(year, month - 1, day, hour, minute, second) - (4 * 60 * 60 * 1000);
      const shifted = new Date(shiftedUtcMs);

      const outMonth = pad(shifted.getUTCMonth() + 1);
      const outDay = pad(shifted.getUTCDate());
      const outYear = shifted.getUTCFullYear();
      const outMinute = pad(shifted.getUTCMinutes());
      const outHour24 = shifted.getUTCHours();
      const outHour12 = outHour24 % 12 || 12;
      const amPm = outHour24 >= 12 ? 'PM' : 'AM';

      return `${outMonth}/${outDay}/${outYear} ${outHour12}:${outMinute} ${amPm}`;
    }

    // Support backend strings like: Wed, 09 Sep 2026 20:15:00 GMT
    const gmtMatch = source.match(/^\w{3},\s*(\d{2})\s*(\w{3})\s*(\d{4})\s*(\d{2}):(\d{2})(?::(\d{2}))?\s*GMT$/i);
      if (gmtMatch) {
        const day = Number(gmtMatch[1]);
        const month = monthMap[gmtMatch[2].slice(0, 1).toUpperCase() + gmtMatch[2].slice(1, 3).toLowerCase()];
        const year = Number(gmtMatch[3]);
        const hour = Number(gmtMatch[4]);
        const minute = Number(gmtMatch[5]);
        const second = Number(gmtMatch[6] || '0');
        if (!month) return source;

        const shiftedUtcMs = Date.UTC(year, month - 1, day, hour, minute, second) - (4 * 60 * 60 * 1000);
        const shifted = new Date(shiftedUtcMs);

        const outMonth = pad(shifted.getUTCMonth() + 1);
        const outDay = pad(shifted.getUTCDate());
        const outYear = shifted.getUTCFullYear();
        const outMinute = pad(shifted.getUTCMinutes());
        const outHour24 = shifted.getUTCHours();
        const outHour12 = outHour24 % 12 || 12;
        const amPm = outHour24 >= 12 ? 'PM' : 'AM';

        return `${outMonth}/${outDay}/${outYear} ${outHour12}:${outMinute} ${amPm}`;
      }
  };

  const fetchBlockouts = async (aircraftId) => {
    try {
      const today = new Date();
      const startDate = new Date(today);
      const endDate = new Date(today);
      endDate.setFullYear(endDate.getFullYear() + 1);
      const fmt = d => d.toISOString().split('T')[0];
      const response = await fetch(`${API_BASE_URL}/aircraft-blockout/aircraft/${aircraftId}/${fmt(startDate)}/${fmt(endDate)}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setBlockouts(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      setBlockouts([]);
    } finally {
      setLoadingBlockouts(false);
    }
  };

  const saveBlockout = async (data) => {
    try {
      // datetime-local inputs give local time strings with no timezone.
      // Convert to UTC ISO strings so the backend stores the correct absolute time.
      const toUTC = (dt) => dt ? new Date(dt).toISOString() : dt;
      const payload = {
        aircraftId: editingId,
        startDatetime: toUTC(data.startDatetime),
        endDatetime: toUTC(data.endDatetime),
        reason: data.reason || ''
      };
      if (data.id) payload.id = data.id;
      const response = await fetch(`${API_BASE_URL}/aircraft-blockout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'session-id': currentUser?.sessionId },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        setBlockoutMessage('Blockout saved successfully');
        setIsAddingBlockout(false);
        setEditingBlockoutId(null);
        setNewBlockoutData({ startDatetime: '', endDatetime: '', reason: '' });
        fetchBlockouts(editingId);
        setTimeout(() => setBlockoutMessage(''), 3000);
      } else {
        const err = await response.text();
        setBlockoutMessage(`Error: ${err}`);
        setTimeout(() => setBlockoutMessage(''), 5000);
      }
    } catch (error) {
      setBlockoutMessage(`Error: ${error.message}`);
      setTimeout(() => setBlockoutMessage(''), 5000);
    }
  };

  const deleteBlockout = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/aircraft-blockout/${id}`, {
        method: 'DELETE',
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        setBlockoutMessage('Blockout deleted');
        fetchBlockouts(editingId);
        setTimeout(() => setBlockoutMessage(''), 3000);
      } else {
        const err = await response.text();
        setBlockoutMessage(`Error: ${err}`);
        setTimeout(() => setBlockoutMessage(''), 5000);
      }
    } catch (error) {
      setBlockoutMessage(`Error: ${error.message}`);
      setTimeout(() => setBlockoutMessage(''), 5000);
    }
  };

  const fetchMaintenanceRecords = async (aircraftId) => {
    setLoadingMaintenance(true);
    try {
      const response = await fetch(`${API_BASE_URL}/aircraft-maintenance/aircraft/${aircraftId}/${maintenanceStartDate}/${maintenanceEndDate}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      if (response.ok) {
        const data = await response.json();
        setMaintenanceRecords(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      setMaintenanceRecords([]);
    } finally {
      setLoadingMaintenance(false);
    }
  };

  const fetchSquawks = async (aircraftId) => {
    setLoadingSquawks(true);
    try {
      const response = await fetch(`${API_BASE_URL}/squak/aircraft/${aircraftId}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        const records = Array.isArray(data) ? data : [];
        records.sort((a, b) => new Date(b.reportedDatetime || 0) - new Date(a.reportedDatetime || 0));
        setSquawks(records);
      } else {
        setSquawks([]);
      }
    } catch (error) {
      setSquawks([]);
    } finally {
      setLoadingSquawks(false);
    }
  };

  const saveSquawk = async (data) => {
    const payload = {
      aircraftId: editingId,
      status: data.status || null,
      fixedDate: data.fixedDate || null,
      issue: data.issue || null,
      details: data.details || null
    };
    if (data.id) payload.id = data.id;
    if (data.id) {
      const isoReported = toIsoDatetime(data.reportedDatetime);
      if (isoReported) payload.reportedDatetime = isoReported;
    } else {
      payload.reportedDatetime = new Date().toISOString();
    }

    try {
      const response = await fetch(`${API_BASE_URL}/squak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'session-id': currentUser?.sessionId },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        setSquawkMessage('Squawk saved successfully');
        setIsAddingSquawk(false);
        setNewSquawkData({ reportedDatetime: '', fixedDate: '', status: 'New', issue: '', details: '' });
        setEditingSquawkId(null);
        setEditingSquawkData({});
        fetchSquawks(editingId);
        setTimeout(() => setSquawkMessage(''), 3000);
      } else {
        const errText = await response.text();
        setSquawkMessage(`Error: ${errText || 'Failed to save squawk'}`);
        setTimeout(() => setSquawkMessage(''), 5000);
      }
    } catch (error) {
      setSquawkMessage(`Error: ${error.message || 'Failed to save squawk'}`);
      setTimeout(() => setSquawkMessage(''), 5000);
    }
  };

  const fetchAircraftExpenses = async (aircraftId) => {
    setLoadingExpenses(true);
    try {
      const response = await fetch(`${API_BASE_URL}/expense/aircraft/${aircraftId}/${expensesStartDate}/${expensesEndDate}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAircraftExpenses(Array.isArray(data) ? data : []);
      } else {
        setAircraftExpenses([]);
      }
    } catch (error) {
      setAircraftExpenses([]);
    } finally {
      setLoadingExpenses(false);
    }
  };

  const fetchAircraftReservations = async (aircraftId) => {
    setLoadingReservations(true);
    try {
      const response = await fetch(`${API_BASE_URL}/reservation/aircraft/${aircraftId}/${reservationsStartDate}/${reservationsEndDate}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAircraftReservations(Array.isArray(data) ? data : []);
      } else {
        setAircraftReservations([]);
      }
    } catch (error) {
      setAircraftReservations([]);
    } finally {
      setLoadingReservations(false);
    }
  };

  const fetchLatestTachs = async (aircraftList) => {
    if (!currentUser?.sessionId) {
      setLatestTachByAircraft({});
      return;
    }

    const latestTachEntries = await Promise.all((Array.isArray(aircraftList) ? aircraftList : []).map(async (plane) => {
      let page = 1;
      let latestEntry = null;
      let hasMoreRecords = true;

      while (hasMoreRecords) {
        try {
          const response = await fetch(`${API_BASE_URL}/aircraft/${plane.id}/log/${page}/100`, {
            headers: { 'session-id': currentUser.sessionId }
          });
          if (!response.ok) break;

          const data = await response.json();
          const records = Array.isArray(data) ? data : [];
          records.forEach((entry) => {
            const tachEnd = Number(entry.tachEnd);
            if (!Number.isFinite(tachEnd)) return;

            const entryTime = new Date(entry.date || entry.createdAt || 0).getTime() || 0;
            const latestTime = latestEntry ? new Date(latestEntry.date || latestEntry.createdAt || 0).getTime() || 0 : -1;
            if (!latestEntry || entryTime > latestTime || (entryTime === latestTime && Number(entry.id) > Number(latestEntry.id))) {
              latestEntry = entry;
            }
          });

          hasMoreRecords = records.length === 100;
          page += 1;
        } catch (error) {
          break;
        }
      }

      return latestEntry ? [plane.id, Number(latestEntry.tachEnd)] : null;
    }));

    setLatestTachByAircraft(Object.fromEntries(latestTachEntries.filter(Boolean)));
  };

  React.useEffect(() => {
    fetchAircraft();
  }, []);

  React.useEffect(() => {
    if (!showForm) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [showForm]);

  React.useEffect(() => {
    const displayAircraft = currentUser?.user?.orgRole === 'client' ? ownedAircraft : aircraft;
    fetchLatestTachs(displayAircraft);
  }, [aircraft, ownedAircraft, currentUser?.sessionId, currentUser?.user?.orgRole]);

  React.useEffect(() => {
    if (activeTab === 'maintenance' && editingId) {
      fetchMaintenanceRecords(editingId);
    }
  }, [activeTab, editingId, maintenanceStartDate, maintenanceEndDate]);

  React.useEffect(() => {
    if (activeTab === 'expenses' && editingId) {
      fetchAircraftExpenses(editingId);
    }
  }, [activeTab, editingId, expensesStartDate, expensesEndDate]);

  React.useEffect(() => {
    if (activeTab === 'reservations' && editingId) {
      fetchAircraftReservations(editingId);
    }
  }, [activeTab, editingId, reservationsStartDate, reservationsEndDate]);

  const handleMouseDown = (e) => {
    const bounds = e.currentTarget.parentElement.parentElement.getBoundingClientRect();
    setIsDragging(true);
    setDialogPos({ x: bounds.left, y: bounds.top });
    setDragOffset({ x: e.clientX - bounds.left, y: e.clientY - bounds.top });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setDialogPos({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);
  const [formData, setFormData] = useState(emptyAircraftFormData);

  const getGcsMediaDownloadUrl = (imageUrl) => {
    try {
      const url = new URL(imageUrl);
      if (url.hostname !== 'storage.googleapis.com') return null;
      const pathParts = url.pathname.split('/').filter(Boolean);
      const bucket = pathParts.shift();
      const objectName = pathParts.join('/');
      if (!bucket || !objectName) return null;
      return `https://storage.googleapis.com/download/storage/v1/b/${encodeURIComponent(bucket)}/o/${encodeURIComponent(objectName)}?alt=media`;
    } catch (error) {
      return null;
    }
  };

  const getAircraftImageFilename = (imageUrl, index) => {
    try {
      const url = new URL(imageUrl);
      const filename = decodeURIComponent(url.pathname.split('/').filter(Boolean).pop() || '');
      return filename || `image-${index + 1}`;
    } catch (error) {
      return `image-${index + 1}`;
    }
  };

  const prepareExistingAircraftImage = async (image, index) => {
    const originalFilename = getAircraftImageFilename(image.imageUrl, index);
    const gcsMediaUrl = getGcsMediaDownloadUrl(image.imageUrl);
    const downloadUrls = [gcsMediaUrl, image.imageUrl].filter(Boolean);
    let response = null;
    for (const downloadUrl of downloadUrls) {
      try {
        const candidateResponse = await fetch(downloadUrl, { credentials: 'omit', cache: 'force-cache' });
        if (candidateResponse.ok) {
          response = candidateResponse;
          break;
        }
      } catch (error) {
      }
    }
    if (!response) throw new Error(`Unable to download existing image ${index + 1}. Configure GCS CORS or provide an API endpoint that returns the image bytes.`);

    const sourceBlob = await response.blob();
    if (['image/png', 'image/jpeg'].includes(sourceBlob.type) && sourceBlob.size <= 1024 * 1024) {
      return new File([sourceBlob], originalFilename, { type: sourceBlob.type });
    }

    const bitmap = await createImageBitmap(sourceBlob);
    let scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
    for (let attempt = 0; attempt < 6; attempt += 1) {
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const context = canvas.getContext('2d');
      context.fillStyle = '#fff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const quality = Math.max(0.5, 0.88 - attempt * 0.08);
      const jpegBlob = await new Promise((resolve, reject) => canvas.toBlob((result) => result ? resolve(result) : reject(new Error(`Unable to convert existing image ${index + 1}`)), 'image/jpeg', quality));
      if (jpegBlob.size <= 1024 * 1024) {
        bitmap.close();
        return new File([jpegBlob], originalFilename, { type: 'image/jpeg' });
      }
      scale *= 0.8;
    }
    bitmap.close();
    throw new Error(`Existing image ${index + 1} could not be reduced below 1 MB`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSavingAircraft) return;
    setMessage('');
    setWebsiteError('');
    if (activeTab === 'website' && (!formData.registration.trim() || !formData.manufacturer.trim() || !formData.model.trim())) {
      setMessage('Enter the registration, manufacturer, and model on the Aircraft tab before saving.');
      setActiveTab('aircraft');
      return;
    }
    if (isAdminOrSuperuser) {
      try { validateAircraftWebsiteLists(formData); }
      catch (error) { setWebsiteError(error.message); setActiveTab('aircraft'); return; }
    }
    setIsSavingAircraft(true);
    const normalizedSortOrder = formData.sortOrder === '' ? null : parseInt(formData.sortOrder, 10);
    
    try {
      const payload = {
        make: formData.manufacturer,
        model: formData.model,
        tailNumber: formData.registration,
        year: parseInt(formData.year) || 0,
        sortOrder: normalizedSortOrder,
        sort_order: normalizedSortOrder,
        type: formData.type,
        color: formData.color,
        equipment: formData.equipment,
        rate: parseFloat(formData.hourlyRate) || 0,
        ownerRate: parseFloat(formData.ownerRate) || 0,
        engineRate: parseFloat(formData.engineRate) || 0,
        ownerId: formData.ownerId || undefined,
        status: formData.status
      };
      if (editingId) {
        payload.id = editingId;
      }

      const multipartData = new FormData();
      const aircraftFacts = {
        aircraftClass: formData.aircraftClass || null,
        serialNumber: formData.serialNumber.trim() || null,
        ifrEquipped: formData.ifrEquipped === '' ? null : formData.ifrEquipped === 'true'
      };
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null) multipartData.append(key, value);
      });
      if (isAdminOrSuperuser) appendAircraftWebsiteFields(multipartData, formData);
      if (isAdminOrSuperuser) {
        Object.entries(aircraftFacts).forEach(([key, value]) => multipartData.append(key, value === null ? 'null' : String(value)));
      }
      if (aircraftImagesChanged) {
        if (aircraftImages.length === 0) {
          setMessage('Error: The API cannot remove the final aircraft image without a replacement image');
          return;
        }
        for (let index = 0; index < aircraftImages.length; index += 1) {
          const image = aircraftImages[index];
          const imageFile = image.file || await prepareExistingAircraftImage(image, index);
          multipartData.append('aircraftImages', imageFile, imageFile.name);
        }
      }
      
      const response = await fetch(`${API_BASE_URL}/aircraft`, {
        method: 'POST',
        headers: { 'session-id': currentUser?.sessionId, ...(!aircraftImagesChanged ? { 'Content-Type': 'application/json' } : {}) },
        body: aircraftImagesChanged ? multipartData : JSON.stringify({
          ...Object.fromEntries(multipartData),
          ...(isAdminOrSuperuser ? aircraftFacts : {})
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        const savedSortOrder = normalizedSortOrder;
        const savedImages = Array.isArray(result.images) ? result.images : aircraftImages;
        const websiteValues = isAdminOrSuperuser ? {
          showOnWebsite: !!formData.showOnWebsite,
          websiteTitle: formData.websiteTitle || null,
          priceLabel: formData.priceLabel || null,
          websiteDescription: formData.websiteDescription || null,
          avionics: parseAircraftWebsiteList(formData.avionics),
          features: parseAircraftWebsiteList(formData.features),
          rateDescription: formData.rateDescription || null
        } : {};
        const updatedAircraftData = { ...formData, ...(isAdminOrSuperuser ? aircraftFacts : {}), ...result, ...websiteValues, sortOrder: savedSortOrder, imageUrl: result.imageUrl || savedImages[0]?.imageUrl || '', images: savedImages };
        if (editingId) {
          updateAircraft(editingId, updatedAircraftData);
          setMessage(`Aircraft ${formData.registration} has been updated successfully`);
        } else {
          addAircraft(updatedAircraftData);
          fetchAircraft(); // Refresh the aircraft list
          const newAircraftId = result.id || result.aircraftId;
          setEditingId(newAircraftId);
          setMessage(`Aircraft ${formData.registration} has been added successfully`);
        }
        
        setTimeout(() => {
          aircraftImages.forEach((image) => {
            if (image.isNew && image.imageUrl?.startsWith('blob:')) URL.revokeObjectURL(image.imageUrl);
          });
          setShowForm(false);
          setEditingId(null);
          setAircraftImages([]);
          setAircraftImagesChanged(false);
          setAircraftImageModalIndex(null);
          setFormData(emptyAircraftFormData());
          setMessage('');
        }, 3000);
      } else {
        const errorText = await response.text();
        let errorMessage = errorText || 'Failed to save aircraft';
        try { errorMessage = JSON.parse(errorText).error || errorMessage; } catch { /* Keep plain-text API errors readable. */ }
        if (response.status === 400 && /aircraftClass|ifrEquipped|serialNumber/.test(errorMessage)) { setMessage(errorMessage); setActiveTab('aircraft'); }
        else if (response.status === 400 && isAdminOrSuperuser) { setWebsiteError(errorMessage); setActiveTab('website'); }
        else setMessage(errorMessage);
      }
    } catch (error) {
      setMessage(`Error: ${error.message || 'Error saving aircraft'}`);
    } finally {
      setIsSavingAircraft(false);
    }
  };

  const selectAircraftImages = (files) => {
    const selectedFiles = Array.from(files || []);
    if (selectedFiles.length === 0) return;
    const invalidFile = selectedFiles.find((file) => !['image/png', 'image/jpeg'].includes(file.type) || file.size > 1024 * 1024);
    if (invalidFile) {
      setMessage(`Error: ${invalidFile.name} must be PNG or JPEG and 1 MB or smaller`);
      return;
    }
    const newImages = selectedFiles.map((file) => ({ file, imageUrl: URL.createObjectURL(file), isNew: true }));
    setAircraftImages((previous) => [...previous, ...newImages]);
    setAircraftImagesChanged(true);
    setMessage('');
  };

  const removeAircraftImage = (index) => {
    setAircraftImages((previous) => {
      const removedImage = previous[index];
      if (removedImage?.isNew && removedImage.imageUrl?.startsWith('blob:')) URL.revokeObjectURL(removedImage.imageUrl);
      return previous.filter((_, imageIndex) => imageIndex !== index);
    });
    setAircraftImagesChanged(true);
    setAircraftImageModalIndex(null);
  };

  const setPrimaryAircraftImage = (index) => {
    if (index === 0) return;
    setAircraftImages((previous) => {
      const selectedImage = previous[index];
      return [selectedImage, ...previous.filter((_, imageIndex) => imageIndex !== index)];
    });
    setAircraftImagesChanged(true);
    setAircraftImageModalIndex(null);
  };

  const fetchAircraftLog = async (aircraftId, page = 1, perPage = 25) => {
    setLoadingLog(true);
    try {
      const response = await fetch(`${API_BASE_URL}/aircraft/${aircraftId}/log/${page}/${perPage}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAircraftLog(data);
      }
    } catch (error) {
    } finally {
      setLoadingLog(false);
    }
  };

  const handleEditLog = (entry) => {
    if (editingLogId || isAddingNew) return;
    setEditingLogId(entry.id);
    setEditingLogData({
      ...entry,
      date: entry.date ? entry.date.split('T')[0] : ''
    });
    setLogMessage('');
  };

  const handleSaveLog = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/aircraft-log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(editingLogData)
      });
      if (response.ok) {
        const updatedEntry = await response.json();
        setAircraftLog(aircraftLog.map(entry => 
          entry.id === editingLogId ? updatedEntry : entry
        ));
        setLogMessage('Entry updated successfully');
        setEditingLogId(null);
        setEditingLogData({});
        setTimeout(() => setLogMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setLogMessage(`Error: ${errorText || 'Failed to update entry'}`);
        setTimeout(() => setLogMessage(''), 5000);
      }
    } catch (error) {
      setLogMessage(`Error: ${error.message || 'Failed to update entry'}`);
      setTimeout(() => setLogMessage(''), 5000);
    }
  };

  const handleAddNew = () => {
    if (editingLogId || isAddingNew) return;
    setIsAddingNew(true);
    const lastEntry = aircraftLog[0];
    setNewLogData({ 
      aircraftId: editingId,
      hobbsStart: lastEntry?.hobbsEnd || '',
      tachStart: lastEntry?.tachEnd || ''
    });
    setLogMessage('');
  };

  const handleSaveNew = async () => {
    const updatedLogData = { ...newLogData, aircraftId: editingId };
    try {
      const response = await fetch(`${API_BASE_URL}/aircraft-log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(updatedLogData)
      });
      if (response.ok) {
        const savedEntry = await response.json();
        setAircraftLog([savedEntry, ...aircraftLog]);
        setLogMessage('New entry created successfully');
        setIsAddingNew(false);
        setNewLogData({});
        setTimeout(() => setLogMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setLogMessage(`Error: ${errorText || 'Failed to create entry'}`);
        setTimeout(() => setLogMessage(''), 5000);
      }
    } catch (error) {
      setLogMessage(`Error: ${error.message || 'Failed to create entry'}`);
      setTimeout(() => setLogMessage(''), 5000);
    }
  };

  const handleCancelNew = () => {
    setIsAddingNew(false);
    setNewLogData({});
  };

  const handleAddMaintenance = () => {
    setIsAddingMaintenance(true);
    setNewMaintenanceData({ aircraftId: editingId });
  };

  const setMaintenanceRecordFile = (setter, file) => {
    if (file && file.size > 1048576) {
      setMaintenanceMessage('Error: File size must be under 1MB');
      setTimeout(() => setMaintenanceMessage(''), 5000);
      return;
    }
    setter(prev => ({ ...prev, maintenanceRecordFile: file || null }));
  };

  const buildMaintenanceFormData = (data, id) => {
    const formData = new FormData();
    if (id) formData.append('id', id);
    formData.append('aircraftId', data.aircraftId || editingId || '');
    formData.append('airport', data.airport || '');
    formData.append('maintenanceDate', data.maintenanceDate || '');
    formData.append('hobbs', data.hobbs || '');
    formData.append('tach', data.tach || '');
    formData.append('type', data.type || '');
    formData.append('description', data.description || '');
    formData.append('cost', data.cost || '');
    formData.append('vendor', data.vendor || '');
    formData.append('category', data.category || '');
    if (data.maintenanceRecordFile) {
      formData.append('maintenanceRecordImage', data.maintenanceRecordFile);
    }
    return formData;
  };

  const handleSaveMaintenance = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/aircraft-maintenance`, {
        method: 'POST',
        headers: {
          'session-id': currentUser?.sessionId
        },
        body: buildMaintenanceFormData(newMaintenanceData)
      });
      if (response.ok) {
        const savedRecord = await response.json();
        setMaintenanceRecords([savedRecord, ...maintenanceRecords]);
        setMaintenanceMessage('Maintenance record saved successfully');
        setIsAddingMaintenance(false);
        setNewMaintenanceData({});
        setTimeout(() => setMaintenanceMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setMaintenanceMessage(`Error: ${errorText || 'Failed to save maintenance record'}`);
        setTimeout(() => setMaintenanceMessage(''), 5000);
      }
    } catch (error) {
      setMaintenanceMessage(`Error: ${error.message || 'Failed to save maintenance record'}`);
      setTimeout(() => setMaintenanceMessage(''), 5000);
    }
  };

  const handleEditMaintenance = (record) => {
    if (editingMaintenanceId || isAddingMaintenance) return;
    setEditingMaintenanceId(record.id);
    setEditingMaintenanceData({
      ...record,
      maintenanceDate: record.maintenanceDate ? record.maintenanceDate.split('T')[0] : ''
    });
  };

  const handleSaveEditMaintenance = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/aircraft-maintenance`, {
        method: 'POST',
        headers: {
          'session-id': currentUser?.sessionId
        },
        body: buildMaintenanceFormData(editingMaintenanceData, editingMaintenanceId)
      });
      if (response.ok) {
        const updatedRecord = await response.json();
        setMaintenanceRecords(maintenanceRecords.map(record => 
          record.id === editingMaintenanceId ? updatedRecord : record
        ));
        setMaintenanceMessage('Maintenance record updated successfully');
        setEditingMaintenanceId(null);
        setEditingMaintenanceData({});
        setTimeout(() => setMaintenanceMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setMaintenanceMessage(`Error: ${errorText || 'Failed to update maintenance record'}`);
        setTimeout(() => setMaintenanceMessage(''), 5000);
      }
    } catch (error) {
      setMaintenanceMessage(`Error: ${error.message || 'Failed to update maintenance record'}`);
      setTimeout(() => setMaintenanceMessage(''), 5000);
    }
  };

  const handleCancelMaintenance = () => {
    setIsAddingMaintenance(false);
    setNewMaintenanceData({});
  };

  const handleDeleteMaintenance = (record) => {
    setDeleteMaintenanceRecord(record);
    setShowDeleteMaintenanceModal(true);
  };

  const confirmDeleteMaintenance = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/aircraft-maintenance/${deleteMaintenanceRecord.id}`, {
        method: 'DELETE',
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      if (response.ok) {
        setMaintenanceRecords(maintenanceRecords.filter(record => record.id !== deleteMaintenanceRecord.id));
        setMaintenanceMessage('Maintenance record deleted successfully');
        setTimeout(() => setMaintenanceMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setMaintenanceMessage(`Error: ${errorText || 'Failed to delete maintenance record'}`);
        setTimeout(() => setMaintenanceMessage(''), 5000);
      }
    } catch (error) {
      setMaintenanceMessage(`Error: ${error.message || 'Failed to delete maintenance record'}`);
      setTimeout(() => setMaintenanceMessage(''), 5000);
    }
    setShowDeleteMaintenanceModal(false);
    setDeleteMaintenanceRecord(null);
  };

  const openMaintenanceRecordDocument = async (record) => {
    if (!isAdminOrSuperuser) {
      setMaintenanceMessage('Error: Only admins and superusers can view maintenance documents.');
      return;
    }
    setShowReceiptModal(true);
    setIsLoadingReceipt(true);
    setReceiptModalMessage('');
    setSelectedReceiptImage('');
    setSelectedReceiptType('');

    const imageValue = typeof record?.maintenanceRecordImage === 'string' ? record.maintenanceRecordImage.trim() : '';
    if (imageValue) {
      const resolvedImage = /^https?:\/\//i.test(imageValue) || imageValue.startsWith('data:') || imageValue.startsWith('blob:')
        ? imageValue
        : `${API_BASE_URL}/${imageValue.replace(/^\//, '')}`;
      setSelectedReceiptImage(resolvedImage);
      setSelectedReceiptType(/\.pdf(?:$|\?)/i.test(imageValue) ? 'pdf' : 'image');
      setIsLoadingReceipt(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/aircraft-maintenance/${record.id}/image`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        setSelectedReceiptImage(imageUrl);
        setSelectedReceiptType(blob.type === 'application/pdf' ? 'pdf' : 'image');
      } else {
        const errorText = await response.text();
        setReceiptModalMessage(errorText || 'Unable to load maintenance document');
      }
    } catch (error) {
      setReceiptModalMessage('Unable to load maintenance document');
    }

    setIsLoadingReceipt(false);
  };

  const closeReceiptModal = () => {
    if (selectedReceiptImage.startsWith('blob:')) {
      URL.revokeObjectURL(selectedReceiptImage);
    }
    setShowReceiptModal(false);
    setSelectedReceiptImage('');
    setSelectedReceiptType('');
    setReceiptModalMessage('');
    setIsLoadingReceipt(false);
  };

  const isAdminOrSuperuser = currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser';
  const isInstructor = currentUser?.user?.role === 'instructor' || currentUser?.user?.roleId === 3;
  React.useEffect(() => {
    if (!isAdminOrSuperuser) return undefined;
    const organizationKey = String(currentUser?.user?.orgId ?? '');
    const cachedKey = getCachedOrganizationWidgetKey(organizationKey);
    setAircraftWidgetKey(cachedKey || '');
    setAircraftWidgetError(null);
    setWidgetCopyMessage('');
    if (cachedKey) return undefined;
    let active = true;
    setAircraftWidgetLoading(true);
    fetch(`${API_BASE_URL}/aircraft/widget`, { headers: { 'session-id': currentUser?.sessionId } })
      .then(async response => {
        let data = {};
        try { data = await response.json(); } catch { /* Use the status-based fallback below. */ }
        if (!response.ok) {
          const error = new Error(data?.error || `Unable to load aircraft widget (HTTP ${response.status}).`);
          error.status = response.status;
          throw error;
        }
        if (!data.widgetKey) throw new Error('The aircraft widget API did not return a widget key.');
        if (active) {
          cacheOrganizationWidgetKey(organizationKey, data.widgetKey);
          setAircraftWidgetKey(data.widgetKey);
        }
      })
      .catch(error => { if (active) setAircraftWidgetError(error); })
      .finally(() => { if (active) setAircraftWidgetLoading(false); });
    return () => { active = false; };
  }, [isAdminOrSuperuser, currentUser?.sessionId, currentUser?.user?.orgId, aircraftWidgetRetry]);

  const aircraftWidgetSnippet = buildAircraftWidgetSnippet({
    widgetKey: aircraftWidgetKey,
    scope: widgetScope,
    locationCode: widgetLocationCode,
    overrides: widgetStyleOverrides
  });
  const copyAircraftWidgetSnippet = async () => {
    try {
      await navigator.clipboard.writeText(aircraftWidgetSnippet);
      setWidgetCopyMessage('Embed code copied.');
    } catch {
      aircraftWidgetSnippetRef.current?.focus();
      aircraftWidgetSnippetRef.current?.select();
      setWidgetCopyMessage('Clipboard unavailable. The embed code is selected for copying.');
    }
  };
  React.useEffect(() => {
    if (widgetLocationCode || !locations.length) return;
    setWidgetLocationCode(locationAirportCode(locations.find(location => locationAirportCode(location))) || '');
  }, [locations, widgetLocationCode]);
  const currentEditingAircraft = (Array.isArray(aircraft) ? aircraft : []).find(a => a.id === editingId)
    || (Array.isArray(ownedAircraft) ? ownedAircraft : []).find(a => a.id === editingId);
  const isOwnerOfEditingAircraft = Number(currentEditingAircraft?.ownerId) === Number(currentUser?.user?.id)
    || (Array.isArray(ownedAircraft) ? ownedAircraft : []).some(a => a.id === editingId);

  const loadOwnerStatements = async (aircraftId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/owner-statement/aircraft/${aircraftId}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setOwnerStatements(data);
      }
    } catch (error) {
      console.error('Error loading owner statements:', error);
    }
  };

  const saveOwnerStatement = async (statementData) => {
    try {
      const payload = {
        startDate: statementData.startDate,
        endDate: statementData.endDate,
        ownerId: statementData.ownerId,
        aircraftId: editingId,
        ownerDisbursementTotal: parseFloat(statementData.ownerDisbursementTotal) || 0,
        engineReserveTotal: parseFloat(statementData.engineReserveTotal) || 0,
        upgradeTotal: parseFloat(statementData.upgradeTotal) || 0
      };
      
      if (statementData.id && !statementData.id.toString().startsWith('temp_')) {
        payload.id = statementData.id;
      }
      
      const response = await fetch(`${API_BASE_URL}/owner-statement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setOwnerStatementMessage('Owner statement saved successfully');
        loadOwnerStatements(editingId);
        setEditingOwnerStatementId(null);
        setTimeout(() => setOwnerStatementMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setOwnerStatementMessage(`Error: ${errorText}`);
        setTimeout(() => setOwnerStatementMessage(''), 5000);
      }
    } catch (error) {
      setOwnerStatementMessage(`Error: ${error.message}`);
      setTimeout(() => setOwnerStatementMessage(''), 5000);
    }
  };

  const updateOwnerStatementField = (statementId, field, value) => {
    setOwnerStatements(prev => prev.map(statement => 
      statement.id === statementId ? { ...statement, [field]: value } : statement
    ));
  };

  const addNewOwnerStatement = () => {
    const tempId = `temp_${Date.now()}`;
    const currentAircraft = (Array.isArray(aircraft) ? aircraft : []).find(a => a.id === editingId);
    const newStatement = {
      id: tempId,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      ownerId: currentAircraft?.ownerId || '',
      aircraftId: editingId,
      ownerDisbursementTotal: '',
      engineReserveTotal: '',
      upgradeTotal: ''
    };
    setOwnerStatements([newStatement, ...ownerStatements]);
    setEditingOwnerStatementId(tempId);
  };

  const fetchOwnerExpenses = async (aircraftId) => {
    setLoadingOwnerExpenses(true);
    try {
      const response = await fetch(`${API_BASE_URL}/aircraft-owner-expense/aircraft/${aircraftId}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setOwnerExpenses(Array.isArray(data) ? data : []);
      } else {
        setOwnerExpenses([]);
      }
    } catch (error) {
      setOwnerExpenses([]);
    } finally {
      setLoadingOwnerExpenses(false);
    }
  };

  const saveOwnerExpense = async (isNew) => {
    const data = isNew ? newOwnerExpenseData : editingOwnerExpenseData;
    const currentAircraft = (Array.isArray(aircraft) ? aircraft : []).find(a => a.id === editingId);
    const payload = {
      ...data,
      aircraftId: editingId,
      ownerId: data.ownerId || currentAircraft?.ownerId,
      amount: parseFloat(data.amount) || 0
    };
    try {
      const response = await fetch(`${API_BASE_URL}/aircraft-owner-expense`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'session-id': currentUser?.sessionId },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        const saved = await response.json();
        if (isNew) {
          setOwnerExpenses([saved, ...ownerExpenses]);
          setIsAddingOwnerExpense(false);
          setNewOwnerExpenseData({});
        } else {
          setOwnerExpenses(ownerExpenses.map(e => e.id === editingOwnerExpenseId ? saved : e));
          setEditingOwnerExpenseId(null);
          setEditingOwnerExpenseData({});
        }
        setOwnerExpenseMessage('Saved successfully');
        setTimeout(() => setOwnerExpenseMessage(''), 3000);
      } else {
        const err = await response.text();
        setOwnerExpenseMessage(`Error: ${err}`);
        setTimeout(() => setOwnerExpenseMessage(''), 5000);
      }
    } catch (error) {
      setOwnerExpenseMessage(`Error: ${error.message}`);
      setTimeout(() => setOwnerExpenseMessage(''), 5000);
    }
  };

  const confirmDeleteOwnerExpense = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/aircraft-owner-expense/${deleteOwnerExpenseRecord.id}`, {
        method: 'DELETE',
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        setOwnerExpenses(ownerExpenses.filter(e => e.id !== deleteOwnerExpenseRecord.id));
        setOwnerExpenseMessage('Deleted successfully');
        setTimeout(() => setOwnerExpenseMessage(''), 3000);
      }
    } catch (error) {
      setOwnerExpenseMessage(`Error: ${error.message}`);
      setTimeout(() => setOwnerExpenseMessage(''), 5000);
    }
    setShowDeleteOwnerExpenseModal(false);
    setDeleteOwnerExpenseRecord(null);
  };

  const openOwnerStatementPdf = async (statementId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/owner-statement/${statementId}/pdf`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setOwnerStatementPdfUrl(url);
        setShowOwnerStatementPdfModal(true);
      } else {
        const errText = await response.text();
        alert(`Failed to load PDF: ${response.status} ${errText}`);
      }
    } catch (error) {
      alert(`Error loading PDF: ${error.message}`);
    }
  };

  const loadOwnerStatementItems = async (ownerStatementId) => {
    try {
      const [aircraftLogResponse, maintenanceResponse, itemsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/aircraft-log/owner-statement/${ownerStatementId}`, {
          headers: { 'session-id': currentUser?.sessionId }
        }),
        fetch(`${API_BASE_URL}/aircraft-maintenance/owner-statement/${ownerStatementId}`, {
          headers: { 'session-id': currentUser?.sessionId }
        }),
        fetch(`${API_BASE_URL}/aircraft-owner-expense/owner-statement/${ownerStatementId}`, {
          headers: { 'session-id': currentUser?.sessionId }
        })
      ]);
      if (aircraftLogResponse.ok) {
        setOwnerStatementAircraftLog(await aircraftLogResponse.json());
      }
      if (maintenanceResponse.ok) {
        setOwnerStatementMaintenance(await maintenanceResponse.json());
      }
      if (itemsResponse.ok) {
        setOwnerStatementOwnerExpenses(await itemsResponse.json());
      }
    } catch (error) {
      console.error('Error loading owner statement items:', error);
    }
  };

  const handleOwnerStatementClick = (ownerStatement) => {
    setSelectedOwnerStatement(ownerStatement);
    setOwnerStatementDetailsActiveTab('aircraftLog');
    loadOwnerStatementItems(ownerStatement.id);
    setShowOwnerStatementDetailsDialog(true);
  };

  const handleEdit = (plane) => {
    setFormData({
      registration: plane.registration || plane.tailNumber || '',
      model: plane.model || '',
      manufacturer: plane.manufacturer || plane.make || '',
      year: plane.year || '',
      sortOrder: plane.sortOrder ?? '',
      hourlyRate: plane.hourlyRate || plane.rate || '',
      ownerRate: plane.ownerRate || '',
      engineRate: plane.engineRate || '',
      ownerId: plane.ownerId || '',
      status: plane.status || 'Available',
      equipment: plane.equipment || '',
      aircraftClass: plane.aircraftClass ?? '',
      serialNumber: plane.serialNumber ?? '',
      ifrEquipped: typeof plane.ifrEquipped === 'boolean' ? String(plane.ifrEquipped) : '',
      color: plane.color || '',
      type: plane.type || '',
      showOnWebsite: !!plane.showOnWebsite,
      websiteTitle: plane.websiteTitle || '',
      priceLabel: plane.priceLabel || '',
      websiteDescription: plane.websiteDescription || '',
      avionics: Array.isArray(plane.avionics) ? plane.avionics.join('\n') : '',
      features: Array.isArray(plane.features) ? plane.features.join('\n') : '',
      rateDescription: plane.rateDescription || ''
    });
    const existingImages = Array.isArray(plane.images) && plane.images.length > 0
      ? plane.images.slice().sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
      : plane.imageUrl ? [{ imageUrl: plane.imageUrl, sortOrder: 1 }] : [];
    setAircraftImages(existingImages);
    setAircraftImagesChanged(false);
    setAircraftImageModalIndex(null);
    setWebsiteError('');
    setWidgetScope('all');
    setWidgetStyleOverrides({ headingFont: '', headingColor: '', textFont: '', textColor: '', accentColor: '', bandColor: '', currency: '' });
    setWidgetLocationCode(locationAirportCode(locations.find(location => locationAirportCode(location))) || '');
    setActiveTab('aircraft');
    setDialogPos(null);
    setEditingId(plane.id);
    setShowForm(true);
  };

  const handleCancel = () => {
    setFormData(emptyAircraftFormData());
    setEditingId(null);
    aircraftImages.forEach((image) => {
      if (image.isNew && image.imageUrl?.startsWith('blob:')) URL.revokeObjectURL(image.imageUrl);
    });
    setAircraftImages([]);
    setAircraftImagesChanged(false);
    setAircraftImageModalIndex(null);
    setWebsiteError('');
    setWidgetScope('all');
    setMessage('');
    setShowForm(false);
    setActiveTab('aircraft');
    setDialogPos(null);
    setAircraftLog([]);
    setAircraftExpenses([]);
    setAircraftReservations([]);
    setSquawks([]);
    setSquawkMessage('');
    setIsAddingSquawk(false);
    setNewSquawkData({ reportedDatetime: '', fixedDate: '', status: 'New', issue: '', details: '' });
    setEditingSquawkId(null);
    setEditingSquawkData({});
  };

  const handleDelete = (plane) => {
    const displayName = plane.tailNumber || plane.registration || `${plane.make || plane.manufacturer} ${plane.model}`;
    setDeleteAircraftData({ id: plane.id, displayName });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/aircraft/${deleteAircraftData.id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        deleteAircraft(deleteAircraftData.id);
        fetchAircraft();
      }
    } catch (error) {
    }
    setShowDeleteModal(false);
    setDeleteAircraftData(null);
  };

  const aircraftTabGroups = [
    {
      key: 'aircraft', label: 'Aircraft', subtabs: [
        { key: 'aircraft', label: 'Details' },
        ...(isAdminOrSuperuser ? [{ key: 'website', label: 'Website' }] : [])
      ]
    },
    ...(editingId ? [{
      key: 'operations', label: 'Operations', subtabs: [
        { key: 'availability', label: 'Availability' },
        { key: 'reservations', label: 'Reservations' },
        ...((isAdminOrSuperuser || isOwnerOfEditingAircraft) ? [{ key: 'blockouts', label: 'Blockouts', onSelect: () => fetchBlockouts(editingId) }] : [])
      ]
    }, {
      key: 'records', label: 'Records', subtabs: [
        { key: 'log', label: 'Aircraft Log', onSelect: () => { setLogPage(1); fetchAircraftLog(editingId, 1, recordsPerPage); } },
        ...((isAdminOrSuperuser || isInstructor || isOwnerOfEditingAircraft) ? [
          { key: 'maintenance', label: 'Maintenance' },
          { key: 'squawks', label: 'Squawks', onSelect: () => fetchSquawks(editingId) }
        ] : [])
      ]
    }, {
      key: 'finance', label: 'Finance', subtabs: [
        ...((isAdminOrSuperuser || isOwnerOfEditingAircraft) ? [
          { key: 'expenses', label: 'Expenses' },
          { key: 'ownerExpenses', label: 'Owner Expenses', onSelect: () => fetchOwnerExpenses(editingId) },
          { key: 'ownerStatements', label: 'Owner Statements', onSelect: () => loadOwnerStatements(editingId) }
        ] : [])
      ]
    }] : [])
  ].filter(group => group.subtabs.length > 0);
  const activeAircraftTabGroup = aircraftTabGroups.find(group => group.subtabs.some(tab => tab.key === activeTab)) || aircraftTabGroups[0];
  const selectAircraftTab = tab => {
    setActiveTab(tab.key);
    tab.onSelect?.();
  };
  const selectAircraftTabGroup = group => {
    const tab = group.subtabs.find(item => item.key === activeTab) || group.subtabs[0];
    if (tab) selectAircraftTab(tab);
  };



  return (
    <div>
      <WelcomeMessage />
      <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column' }}>
      <div style={{ order: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2>Aircraft</h2>
        {isAdminOrSuperuser && <button onClick={() => { setFormData(emptyAircraftFormData()); setEditingId(null); setActiveTab('aircraft'); setWebsiteError(''); setWidgetScope('all'); setWidgetLocationCode(locationAirportCode(locations.find(location => locationAirportCode(location))) || ''); setDialogPos(null); setShowForm(true); }}>Add Aircraft</button>}
      </div>

      {isAdminOrSuperuser && <section aria-label="Aircraft website widget" style={{ order: 2, marginBottom: '1.5rem', padding: '1rem', border: '1px solid #cbd9e0', borderRadius: '6px', backgroundColor: '#f3f7f9' }}>
        <h3 style={{ margin: '0 0 0.75rem' }}>Website widget</h3>
        {aircraftWidgetLoading && <p role="status">Loading website widget...</p>}
        {aircraftWidgetError && <div role="alert" style={{ color: '#b02a37', marginBottom: '0.75rem' }}>{aircraftWidgetError.status === 503 ? 'Website feature not set up yet (database migration pending).' : aircraftWidgetError.message}<button type="button" onClick={() => setAircraftWidgetRetry(value => value + 1)} style={{ marginLeft: '0.5rem' }}>Retry</button></div>}
        {!aircraftWidgetLoading && !aircraftWidgetKey && !aircraftWidgetError && <p role="status">Preparing website widget...</p>}
        <fieldset disabled={!aircraftWidgetKey} style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.25rem', alignItems: 'center', border: 0, padding: 0, margin: '0.75rem 0' }}>
          <legend>Show</legend>
          <label><input type="radio" name="aircraft-widget-scope" value="all" checked={widgetScope === 'all'} onChange={() => setWidgetScope('all')} /> All website aircraft</label>
          <label><input type="radio" name="aircraft-widget-scope" value="location" checked={widgetScope === 'location'} onChange={() => setWidgetScope('location')} /> Only aircraft at a location</label>
          {widgetScope === 'location' && <select aria-label="Widget location" value={widgetLocationCode} onChange={event => setWidgetLocationCode(event.target.value)}>
            <option value="">Select airport</option>
            {locations.filter(location => locationAirportCode(location)).map(location => <option key={location.id} value={locationAirportCode(location)}>{locationAirportCode(location)}{location.name ? ` - ${location.name}` : ''}</option>)}
          </select>}
          {widgetScope === 'location' && <small>Uses that location's rate when one is set.</small>}
        </fieldset>
        <label>Embed code<textarea ref={aircraftWidgetSnippetRef} aria-label="Aircraft widget embed code" readOnly rows={2} value={aircraftWidgetSnippet} style={{ width: '100%', fontFamily: 'monospace', boxSizing: 'border-box' }} /></label>
        <button type="button" disabled={!aircraftWidgetKey || (widgetScope === 'location' && !widgetLocationCode)} onClick={copyAircraftWidgetSnippet}>Copy</button>
        {widgetCopyMessage && <span role="status" style={{ marginLeft: '0.5rem' }}>{widgetCopyMessage}</span>}
        <p>Paste where the aircraft list should appear. The widget picks up the website's fonts and colours automatically.</p>
        <p>Only aircraft with 'Show on website' turned on are listed. Changes appear within about a minute.</p>
        <details>
          <summary>Styling overrides (advanced)</summary>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.5rem 1rem', marginTop: '0.75rem' }}>
            {Object.entries({ headingFont: 'Heading font', headingColor: 'Heading color', textFont: 'Text font', textColor: 'Text color', accentColor: 'Accent color', bandColor: 'Band color', currency: 'Currency' }).map(([key, label]) => <label key={key}>{label}<input aria-label={`Widget ${label}`} value={widgetStyleOverrides[key]} onChange={event => setWidgetStyleOverrides(previous => ({ ...previous, [key]: event.target.value }))} /></label>)}
          </div>
        </details>
      </section>}

      <div style={{ order: 1 }}>{(() => {
        const displayAircraft = currentUser?.user?.orgRole === 'client' ? ownedAircraft : aircraft;
        return (
          <>
            <p>Aircraft count: {displayAircraft.length}</p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f0f0f0' }}>
                  <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', width: '40px' }}></th>
                  <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'center', width: '72px' }}>Image</th>
                  <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Make</th>
                  <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Model</th>
                  <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Tail Number</th>
                  <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Year</th>
                  <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Equipment</th>
                  <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Color</th>
                  {isAdminOrSuperuser && <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Rate</th>}
                  <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Next Oil Change</th>
                  <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Next Annual</th>
                  <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Next 100-Hour</th>
                </tr>
              </thead>
              <tbody>
                {displayAircraft.map((plane, index) => (
            <tr key={plane.id || index} 
                style={{ 
                  backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = '#e3f2fd'}
                onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = index % 2 === 0 ? 'white' : '#f8f9fa'}>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'center' }}>
                {isAdminOrSuperuser && <button onClick={() => handleDelete(plane)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '16px' }}>🗑️</button>}
              </td>
              <td style={{ padding: '0.35rem', border: '1px solid #ccc', textAlign: 'center', cursor: 'pointer' }} onClick={() => handleEdit(plane)}>
                {plane.imageUrl ? <img src={plane.imageUrl} alt={`${plane.tailNumber || plane.registration || 'Aircraft'}`} style={{ width: '56px', height: '42px', objectFit: 'cover', borderRadius: '4px', display: 'block', margin: '0 auto' }} /> : ''}
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(plane)}>{plane.make || plane.manufacturer || ''}</td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(plane)}>{plane.model || ''}</td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(plane)}>{plane.tailNumber || plane.registration || ''}</td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(plane)}>{plane.year || ''}</td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(plane)}>{plane.status || ''}</td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(plane)}>{plane.equipment || ''}</td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(plane)}>{plane.color || ''}</td>
              {isAdminOrSuperuser && <td style={{ padding: '0.5rem', border: '1px solid #ccc', cursor: 'pointer' }} onClick={() => handleEdit(plane)}>{plane.rate || plane.hourlyRate || ''}</td>}
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', backgroundColor: (() => {
                const latestTach = latestTachByAircraft[plane.id];
                if (!Number.isFinite(latestTach)) return 'white';
                const lastEvents = [];
                if (plane.nextOilChange && plane.nextOilChange > 0) lastEvents.push(plane.nextOilChange - 50);
                if (plane.nextHundredHour && plane.nextHundredHour > 0) lastEvents.push(plane.nextHundredHour - 100);
                if (!lastEvents.length) return 'white';
                const diff = Math.max(...lastEvents) + 50 - latestTach;
                return diff < 10 ? 'orange' : diff < 15 ? 'yellow' : 'white';
              })() }}>{plane.nextOilChange || ''}</td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>{formatDateMmDdYyyy(plane.nextAnnual)}</td>
              <td style={{ padding: '0.5rem', border: '1px solid #ccc', backgroundColor: (() => {
                const latestTach = latestTachByAircraft[plane.id];
                const diff = plane.nextHundredHour && Number.isFinite(latestTach) && plane.nextHundredHour > 0 ? plane.nextHundredHour - latestTach : null;
                return diff !== null && diff < 10 ? 'orange' : diff !== null && diff < 15 ? 'yellow' : 'white';
              })() }}>{plane.nextHundredHour || ''}</td>

                </tr>
                ))}
              </tbody>
            </table>
          </>
        );
      })()}</div>

      {showForm && (
        <div style={{ position: 'fixed', top: dialogPos ? `${dialogPos.y}px` : '50%', left: dialogPos ? `${dialogPos.x}px` : '50%', transform: dialogPos ? 'none' : 'translate(-50%, -50%)', backgroundColor: '#f0f8ff', padding: '0', border: '1px solid #ccc', zIndex: 1000, width: 'min(900px, calc(100vw - 32px))', height: 'min(90vh, 900px)', maxHeight: 'calc(100vh - 24px)', overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain' }}>
          <div style={{ position: 'sticky', top: 0, zIndex: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0', padding: '1rem', backgroundColor: '#e6f3ff', borderBottom: '1px solid #ccc' }}>
            <h3 style={{ margin: '0', flex: 1, textAlign: 'center', cursor: 'move' }} onMouseDown={handleMouseDown}>
              {editingId ? (
                <>
                  Edit Aircraft
                  {(() => {
                    const currentAircraft = (Array.isArray(aircraft) ? aircraft : []).find(a => a.id === editingId);
                    return currentAircraft?.tailNumber || currentAircraft?.registration ? ` - ${currentAircraft.tailNumber || currentAircraft.registration}` : '';
                  })()
                  }
                </>
              ) : 'Add Aircraft'}
            </h3>
            <button onClick={handleCancel} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '4px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          
          <div style={{ position: 'sticky', top: '62px', zIndex: 19, backgroundColor: '#f8f9fa', padding: '0.5rem 1rem 0', borderBottom: '1px solid #dee2e6' }}>
            <div aria-label="Aircraft dialog categories" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.2rem' }}>
              {aircraftTabGroups.map(group => <button key={group.key} type="button" aria-pressed={activeAircraftTabGroup?.key === group.key} onClick={() => selectAircraftTabGroup(group)} style={{ padding: '0.55rem 0.9rem', border: `1px solid ${activeAircraftTabGroup?.key === group.key ? '#2563eb' : 'transparent'}`, borderRadius: '5px 5px 0 0', marginBottom: '-1px', backgroundColor: activeAircraftTabGroup?.key === group.key ? '#2563eb' : '#edf2f7', color: activeAircraftTabGroup?.key === group.key ? 'white' : '#334155', cursor: 'pointer', fontWeight: activeAircraftTabGroup?.key === group.key ? 600 : 500 }}>{group.label}</button>)}
            </div>
            {!!activeAircraftTabGroup && <div aria-label={`${activeAircraftTabGroup.label} sections`} style={{ display: 'flex', gap: '0.15rem', overflowX: 'auto', paddingTop: '0.5rem' }}>
              {activeAircraftTabGroup.subtabs.map(tab => <button key={tab.key} type="button" aria-pressed={activeTab === tab.key} onClick={() => selectAircraftTab(tab)} style={{ flexShrink: 0, padding: '0.45rem 0.8rem', border: `1px solid ${activeTab === tab.key ? '#cbd5e1' : 'transparent'}`, borderBottom: activeTab === tab.key ? '1px solid white' : '1px solid transparent', borderRadius: '4px 4px 0 0', marginBottom: '-1px', backgroundColor: activeTab === tab.key ? 'white' : 'transparent', color: activeTab === tab.key ? '#1e3a8a' : '#475569', cursor: 'pointer', fontWeight: activeTab === tab.key ? 600 : 500 }}>{tab.label}</button>)}
            </div>}
          </div>
          
          {message && (
            <div style={{ padding: '0.75rem', margin: '1rem', borderRadius: '4px', backgroundColor: message.includes('Error') || message.includes('Failed') ? '#f8d7da' : '#d4edda', color: message.includes('Error') || message.includes('Failed') ? '#721c24' : '#155724', border: `1px solid ${message.includes('Error') || message.includes('Failed') ? '#f5c6cb' : '#c3e6cb'}` }}>
              {message}
            </div>
          )}
          
          {activeTab === 'availability' && <div style={{ padding: '1.5rem' }}><LocationScheduleEditor resourceId={editingId} resourceType="aircraft" readOnly={!isAdminOrSuperuser} baseRate={formData.hourlyRate} /></div>}
          {(activeTab === 'aircraft' || activeTab === 'website') && (
            <form onSubmit={handleSubmit} onInvalid={event => {
              const field = event.target;
              const label = field.labels?.[0]?.textContent || field.getAttribute('aria-label') || field.parentElement?.querySelector('label')?.textContent || 'Aircraft field';
              setMessage(`Error: ${label.replace(/:$/, '')}: ${field.validationMessage}`);
              setActiveTab(field.closest('[aria-label="Aircraft website settings"]') ? 'website' : 'aircraft');
            }} style={{ padding: '2rem', backgroundColor: activeTab === 'website' ? '#e3f0f5' : '#e6f3ff', border: editingId ? '1px solid #ccc' : 'none', borderTop: 'none' }}>
            <div style={{ display: activeTab === 'aircraft' ? 'block' : 'none' }}>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
              <label style={{ paddingTop: '0.35rem' }}>Images:</label>
              <div style={{ flex: 1, maxWidth: '620px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '0.5rem' }}>
                  {aircraftImages.map((image, index) => (
                    <div key={image.id || image.imageUrl} style={{ position: 'relative', width: '88px', height: '66px', border: index === 0 ? '2px solid #007bff' : '1px solid #aaa', borderRadius: '5px', overflow: 'hidden', backgroundColor: 'white' }}>
                      <button type="button" onClick={() => setAircraftImageModalIndex(index)} title={`View image ${index + 1}`} style={{ border: 'none', padding: 0, backgroundColor: 'transparent', cursor: 'zoom-in', width: '100%', height: '100%' }}>
                        <img src={image.imageUrl} alt={`Aircraft ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      </button>
                      {isAdminOrSuperuser && (
                        <button type="button" onClick={() => removeAircraftImage(index)} title="Remove image" style={{ position: 'absolute', top: '2px', right: '2px', width: '20px', height: '20px', padding: 0, border: '1px solid rgba(0,0,0,0.25)', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.92)', color: '#dc3545', cursor: 'pointer', fontWeight: 'bold', lineHeight: 1 }}>×</button>
                      )}
                      {index === 0 && <span style={{ position: 'absolute', left: '2px', bottom: '2px', padding: '1px 4px', borderRadius: '3px', backgroundColor: 'rgba(0,123,255,0.88)', color: 'white', fontSize: '0.62rem' }}>Primary</span>}
                      {index > 0 && isAdminOrSuperuser && <button type="button" onClick={() => setPrimaryAircraftImage(index)} title="Set as primary image" aria-label="Set as primary image" style={{ position: 'absolute', left: '2px', bottom: '2px', width: '22px', height: '22px', padding: 0, border: '1px solid rgba(0,0,0,0.2)', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.92)', color: '#007bff', cursor: 'pointer', fontSize: '15px', lineHeight: 1 }}>①</button>}
                    </div>
                  ))}
                  {aircraftImages.length === 0 && <span style={{ color: '#6c757d', fontSize: '0.8rem', paddingTop: '0.45rem' }}>No images</span>}
                </div>
                {isAdminOrSuperuser && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <input type="file" multiple accept="image/png,image/jpeg" title="PNG or JPEG, maximum 1 MB each" onChange={(event) => { selectAircraftImages(event.target.files); event.target.value = ''; }} style={{ width: '220px', fontSize: '0.8rem' }} />
                    <span style={{ color: aircraftImagesChanged ? '#155724' : '#6c757d', fontSize: '0.75rem' }}>{aircraftImagesChanged ? `${aircraftImages.length} images will save` : aircraftImages.length > 0 ? `${aircraftImages.length} current image${aircraftImages.length === 1 ? '' : 's'}` : 'Choose one or more images'}</span>
                  </div>
                )}
                {isAdminOrSuperuser && <small style={{ display: 'block', marginTop: '0.35rem', textAlign: 'right', color: '#526671' }}>Images appear on the website in this order; the first one is shown first.</small>}
              </div>
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Registration:</label>
              <input type="text" value={formData.registration} onChange={(e) => setFormData({...formData, registration: e.target.value})} required readOnly={!isAdminOrSuperuser} style={{ backgroundColor: !isAdminOrSuperuser ? '#f0f0f0' : undefined }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Manufacturer:</label>
              <input type="text" value={formData.manufacturer} onChange={(e) => setFormData({...formData, manufacturer: e.target.value})} required readOnly={!isAdminOrSuperuser} style={{ backgroundColor: !isAdminOrSuperuser ? '#f0f0f0' : undefined }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Model:</label>
              <input type="text" value={formData.model} onChange={(e) => setFormData({...formData, model: e.target.value})} required readOnly={!isAdminOrSuperuser} style={{ backgroundColor: !isAdminOrSuperuser ? '#f0f0f0' : undefined }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Year:</label>
              <input type="number" value={formData.year} onChange={(e) => setFormData({...formData, year: e.target.value})} readOnly={!isAdminOrSuperuser} style={{ backgroundColor: !isAdminOrSuperuser ? '#f0f0f0' : undefined }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Sort Order:</label>
              <input type="number" value={formData.sortOrder} onChange={(e) => setFormData({...formData, sortOrder: e.target.value})} readOnly={!isAdminOrSuperuser} style={{ backgroundColor: !isAdminOrSuperuser ? '#f0f0f0' : undefined }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Type:</label>
              <input type="text" value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} readOnly={!isAdminOrSuperuser} style={{ backgroundColor: !isAdminOrSuperuser ? '#f0f0f0' : undefined }} />
            </div>
            {isAdminOrSuperuser && (
              <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label>Hourly Rate:</label>
                <input type="number" step="0.01" value={formData.hourlyRate} onChange={(e) => setFormData({...formData, hourlyRate: e.target.value})} readOnly={!isAdminOrSuperuser} style={{ backgroundColor: !isAdminOrSuperuser ? '#f0f0f0' : undefined }} />
              </div>
            )}
            {isAdminOrSuperuser && (
              <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label>Owner Rate:</label>
                <input type="number" step="0.01" value={formData.ownerRate} onChange={(e) => setFormData({...formData, ownerRate: e.target.value})} readOnly={!isAdminOrSuperuser} style={{ backgroundColor: !isAdminOrSuperuser ? '#f0f0f0' : undefined }} />
              </div>
            )}
            {isAdminOrSuperuser && (
              <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label>Engine Rate:</label>
                <input type="number" step="0.01" value={formData.engineRate} onChange={(e) => setFormData({...formData, engineRate: e.target.value})} readOnly={!isAdminOrSuperuser} style={{ backgroundColor: !isAdminOrSuperuser ? '#f0f0f0' : undefined }} />
              </div>
            )}
            {isAdminOrSuperuser && <div aria-label="Website price preview" style={{ margin: '-0.5rem 0 1rem', textAlign: 'right', color: '#526671', fontSize: '0.9rem' }}>${formData.hourlyRate || 0}/hour{formData.rateDescription ? ` ${formData.rateDescription}` : ''}</div>}
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Status:</label>
              <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} disabled={!isAdminOrSuperuser} style={{ backgroundColor: !isAdminOrSuperuser ? '#f0f0f0' : undefined }}>
                <option value="Available">Available</option>
                <option value="Maintenance">Maintenance</option>
                <option value="Out of Service">Out of Service</option>
                <option value="Unavailable">Unavailable</option>
              </select>
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label htmlFor="aircraft-class">Aircraft class:</label>
              <select id="aircraft-class" value={formData.aircraftClass} onChange={event => setFormData({ ...formData, aircraftClass: event.target.value })} disabled={!isAdminOrSuperuser}>
                <option value="">Not specified</option>
                <option value="ASEL">Airplane Single-Engine Land (ASEL)</option>
                <option value="AMEL">Airplane Multiengine Land (AMEL)</option>
              </select>
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label htmlFor="aircraft-serial-number">Serial number:</label>
              <input id="aircraft-serial-number" type="text" maxLength={100} value={formData.serialNumber} onChange={event => setFormData({ ...formData, serialNumber: event.target.value })} readOnly={!isAdminOrSuperuser} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label htmlFor="aircraft-ifr-equipped">IFR equipped:</label>
              <select id="aircraft-ifr-equipped" value={formData.ifrEquipped} onChange={event => setFormData({ ...formData, ifrEquipped: event.target.value })} disabled={!isAdminOrSuperuser}>
                <option value="">Not specified</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Equipment:</label>
              <input type="text" value={formData.equipment} onChange={(e) => setFormData({...formData, equipment: e.target.value})} readOnly={!isAdminOrSuperuser} style={{ backgroundColor: !isAdminOrSuperuser ? '#f0f0f0' : undefined }} />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label>Color:</label>
              <input type="text" value={formData.color} onChange={(e) => setFormData({...formData, color: e.target.value})} readOnly={!isAdminOrSuperuser} style={{ backgroundColor: !isAdminOrSuperuser ? '#f0f0f0' : undefined }} />
            </div>
            </div>
            {isAdminOrSuperuser && <section aria-label="Aircraft website settings" style={{ display: activeTab === 'website' ? 'block' : 'none', margin: '0 auto', maxWidth: '760px', padding: '1rem', border: '1px solid #cbd9e0', borderRadius: '6px', backgroundColor: '#f3f7f9' }}>
              <div style={{ display: activeTab === 'website' ? 'block' : 'none' }}>
              <h4 style={{ margin: '0 0 0.75rem' }}>Website</h4>
              {websiteError && <p role="alert" style={{ color: '#b02a37', margin: '0 0 0.75rem' }}>{websiteError}</p>}
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <input type="checkbox" aria-label="Show on website" checked={!!formData.showOnWebsite} onChange={event => { setFormData({ ...formData, showOnWebsite: event.target.checked }); setWebsiteError(''); }} />
                Show on website
              </label>
              {!formData.showOnWebsite && <p role="status">This aircraft is hidden from the website.</p>}
              {aircraftImages.length === 0 && <p role="status">No photos will be shown.</p>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem 1rem', alignItems: 'start' }}>
                <label>Website title<input aria-label="Website title" maxLength={100} placeholder={`${formData.manufacturer} ${formData.model}`.trim() || 'Cessna 172E'} value={formData.websiteTitle} onChange={event => { setFormData({ ...formData, websiteTitle: event.target.value }); setWebsiteError(''); }} style={{ width: '100%', boxSizing: 'border-box' }} /></label>
                <label>Rate description<input aria-label="Rate description" maxLength={200} placeholder="wet (fuel included)" value={formData.rateDescription} onChange={event => { setFormData({ ...formData, rateDescription: event.target.value }); setWebsiteError(''); }} style={{ width: '100%', boxSizing: 'border-box' }} /></label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', gridColumn: '1 / -1', minWidth: 0 }}>Price label<input aria-label="Aircraft price label" maxLength={100} placeholder={`${formData.websiteTitle.trim() || `${formData.manufacturer} ${formData.model}`.trim()} rental`.trim()} value={formData.priceLabel} onChange={event => { setFormData({ ...formData, priceLabel: event.target.value }); setWebsiteError(''); }} style={{ width: '100%', boxSizing: 'border-box', padding: '0.5rem' }} /><small>Aircraft with the same price label are combined into one price line (shown as a range if rates differ).</small></label>
                <label style={{ gridColumn: '1 / -1' }}>Description<textarea aria-label="Website description" maxLength={5000} rows={3} value={formData.websiteDescription} onChange={event => { setFormData({ ...formData, websiteDescription: event.target.value }); setWebsiteError(''); }} style={{ width: '100%', boxSizing: 'border-box' }} /></label>
                <label>Avionics<textarea aria-label="Website avionics" rows={4} value={formData.avionics} onChange={event => { setFormData({ ...formData, avionics: event.target.value }); setWebsiteError(''); }} style={{ width: '100%', boxSizing: 'border-box' }} /><small>One item per line. Start with '-' for a sub-item.</small></label>
                <label>Features<textarea aria-label="Website features" rows={4} value={formData.features} onChange={event => { setFormData({ ...formData, features: event.target.value }); setWebsiteError(''); }} style={{ width: '100%', boxSizing: 'border-box' }} /><small>One item per line. Start with '-' for a sub-item.</small></label>
              </div>
              </div>
            </section>}
              <div style={{ textAlign: 'center', marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #ccc' }}>
                {message && <p role={message.startsWith('Error:') ? 'alert' : 'status'}>{message}</p>}
                {websiteError && <p role="alert">{websiteError}</p>}
                {isAdminOrSuperuser && <button type="submit" formNoValidate={activeTab === 'website'} disabled={isSavingAircraft} style={{ cursor: isSavingAircraft ? 'not-allowed' : 'pointer', opacity: isSavingAircraft ? 0.7 : 1 }}>
                  {isSavingAircraft ? <><span style={{ display: 'inline-block', marginRight: '0.4rem', animation: 'aircraft-save-spin 1s linear infinite' }}>⚙️</span>Saving...</> : (editingId ? 'Update' : 'Add')}
                </button>}
                <button type="button" onClick={handleCancel} disabled={isSavingAircraft} style={{ marginLeft: '1rem', cursor: isSavingAircraft ? 'not-allowed' : 'pointer', opacity: isSavingAircraft ? 0.7 : 1 }}>Cancel</button>
                <style>{`@keyframes aircraft-save-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              </div>
            </form>
          )}
          
          {activeTab === 'expenses' && (isAdminOrSuperuser || isOwnerOfEditingAircraft) && (
            <div style={{ padding: '2rem', backgroundColor: '#fff0e6', border: '1px solid #ccc', borderTop: 'none', height: '400px', overflow: 'auto' }}>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
                <h4 style={{ margin: 0 }}>Aircraft Expenses</h4>
                <div>
                  <label style={{ marginRight: '0.5rem' }}>Start Date:</label>
                  <input 
                    type="date" 
                    value={expensesStartDate} 
                    onChange={(e) => {
                      setExpensesStartDate(e.target.value);
                      if (editingId) fetchAircraftExpenses(editingId);
                    }}
                    style={{ padding: '0.25rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ marginRight: '0.5rem' }}>End Date:</label>
                  <input 
                    type="date" 
                    value={expensesEndDate} 
                    onChange={(e) => {
                      setExpensesEndDate(e.target.value);
                      if (editingId) fetchAircraftExpenses(editingId);
                    }}
                    style={{ padding: '0.25rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
                <button 
                  onClick={() => editingId && fetchAircraftExpenses(editingId)}
                  style={{ 
                    padding: '0.25rem 0.5rem', 
                    backgroundColor: '#007bff', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer' 
                  }}
                >
                  Refresh
                </button>
              </div>
              {loadingExpenses ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>Loading expenses...</div>
              ) : (
                <div style={{ overflow: 'auto', height: '280px', width: '150%' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f0f0f0', position: 'sticky', top: 0 }}>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Date</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Expense</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Vendor</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>User</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Total</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aircraftExpenses.length === 0 ? (
                        <tr>
                          <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No expenses found</td>
                        </tr>
                      ) : (
                        aircraftExpenses.map((expense, index) => (
                          <tr key={expense.id || index} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa' }}>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              {expense.date ? new Date(expense.date).toLocaleDateString() : ''}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              {expense.expense || ''}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              {expense.vendor || ''}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              {expense.userFirstName || expense.userName || ''}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              {expense.total ? `$${expense.total}` : ''}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'center' }}>
                              {expense.isAttachment ? (
                                <span 
                                  onClick={async () => {
                                    try {
                                      const response = await fetch(`${API_BASE_URL}/expense/${expense.id}/image`, {
                                        headers: {
                                          'session-id': currentUser?.sessionId
                                        }
                                      });
                                      if (response.ok) {
                                        const blob = await response.blob();
                                        const imageUrl = URL.createObjectURL(blob);
                                        setSelectedReceiptImage(imageUrl);
                                        setSelectedReceiptType(blob.type === 'application/pdf' ? 'pdf' : 'image');
                                        setShowReceiptModal(true);
                                      }
                                    } catch (error) {
                                      console.error('Error loading receipt image:', error);
                                    }
                                  }}
                                  style={{ cursor: 'pointer', fontSize: '16px' }}
                                  title="Click to view receipt"
                                >
                                  📎
                                </span>
                              ) : ''}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'reservations' && (
            <div style={{ padding: '2rem', backgroundColor: '#e6f7ff', border: '1px solid #ccc', borderTop: 'none', height: '400px', overflow: 'auto' }}>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1rem' }}>
                <h4 style={{ margin: 0 }}>Aircraft Reservations</h4>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem' }}>Start Date</label>
                  <input type="date" value={reservationsStartDate} onChange={(event) => setReservationsStartDate(event.target.value)} style={{ padding: '0.25rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.25rem' }}>End Date</label>
                  <input type="date" value={reservationsEndDate} onChange={(event) => setReservationsEndDate(event.target.value)} style={{ padding: '0.25rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                </div>
                <button onClick={() => editingId && fetchAircraftReservations(editingId)} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Refresh</button>
              </div>
              {loadingReservations ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>Loading reservations...</div>
              ) : (
                <div style={{ overflow: 'auto', height: '310px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px', backgroundColor: 'white' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f0f0f0', position: 'sticky', top: 0 }}>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Date</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Time</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Client</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Instructor</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Type</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Status</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Comment</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Maneuvers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aircraftReservations.length === 0 ? (
                        <tr>
                          <td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No reservations found</td>
                        </tr>
                      ) : aircraftReservations.map((reservation, index) => {
                        const client = reservation.client || {};
                        const instructor = reservation.instructor || {};
                        const maneuvers = Array.isArray(reservation.maneuvers) ? reservation.maneuvers.map((maneuver) => maneuver.name).filter(Boolean).join(', ') : '';
                        return (
                          <tr key={reservation.id || index} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa' }}>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>{formatDateMmDdYyyy(reservation.startDate)}</td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>{formatReservationTime(reservation.startTime)} - {formatReservationTime(reservation.endTime)}</td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>{`${client.firstName || ''} ${client.lastName || ''}`.trim()}</td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>{`${instructor.firstName || ''} ${instructor.lastName || ''}`.trim()}</td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>{reservation.type || ''}</td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>{reservation.status || ''}</td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc', maxWidth: '220px', wordBreak: 'break-word' }}>{reservation.comment || ''}</td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc', maxWidth: '220px', wordBreak: 'break-word' }}>{maneuvers}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'squawks' && (isAdminOrSuperuser || isInstructor || isOwnerOfEditingAircraft) && (
            <div style={{ padding: '2rem', backgroundColor: '#fff8e6', border: '1px solid #ccc', borderTop: 'none', height: '400px', overflow: 'auto' }}>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
                <h4 style={{ margin: 0 }}>Squawks</h4>
                <button
                  onClick={() => editingId && fetchSquawks(editingId)}
                  style={{ padding: '0.25rem 0.5rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  Refresh
                </button>
                {isAdminOrSuperuser && (
                  <button
                    onClick={() => {
                      if (editingSquawkId) return;
                      setIsAddingSquawk(true);
                      setNewSquawkData({
                        reportedDatetime: new Date().toISOString(),
                        fixedDate: '',
                        status: 'New',
                        issue: '',
                        details: ''
                      });
                    }}
                    disabled={isAddingSquawk || !!editingSquawkId}
                    style={{ padding: '0.25rem 0.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: (isAddingSquawk || !!editingSquawkId) ? 0.5 : 1 }}
                  >
                    Add Squawk
                  </button>
                )}
              </div>

              {squawkMessage && (
                <div style={{ padding: '0.5rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: squawkMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: squawkMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${squawkMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>
                  {squawkMessage}
                </div>
              )}

              {loadingSquawks ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>Loading squawks...</div>
              ) : (
                <div style={{ overflow: 'auto', height: '280px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1080px', backgroundColor: 'white', tableLayout: 'fixed' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f0f0f0', position: 'sticky', top: 0 }}>
                        {isAdminOrSuperuser && <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', width: '80px' }}>Actions</th>}
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', width: '16%' }}>Reported On</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', width: '12%' }}>Fixed Date</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', width: '14%' }}>Status</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', width: '23%' }}>Issue</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isAddingSquawk && (
                        <tr style={{ backgroundColor: '#e8f5e8' }}>
                          {isAdminOrSuperuser && (
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'center' }}>
                              <button onClick={() => saveSquawk(newSquawkData)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', marginRight: '0.25rem' }}>💾</button>
                              <button onClick={() => { setIsAddingSquawk(false); setNewSquawkData({ reportedDatetime: '', fixedDate: '', status: 'New', issue: '', details: '' }); }} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                            </td>
                          )}
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            {toLocalDatetimeDisplay(newSquawkData.reportedDatetime)}
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <input type="date" value={newSquawkData.fixedDate || ''} onChange={(e) => setNewSquawkData({ ...newSquawkData, fixedDate: e.target.value })} style={{ width: '100%', border: 'none' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <select value={newSquawkData.status || 'New'} onChange={(e) => setNewSquawkData({ ...newSquawkData, status: e.target.value })} style={{ width: '100%', border: 'none', backgroundColor: 'transparent' }}>
                              {squawkStatusOptions.map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <input type="text" maxLength={200} value={newSquawkData.issue || ''} onChange={(e) => setNewSquawkData({ ...newSquawkData, issue: e.target.value })} style={{ width: '100%', border: 'none' }} placeholder="Short issue summary" />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <textarea value={newSquawkData.details || ''} onChange={(e) => setNewSquawkData({ ...newSquawkData, details: e.target.value })} rows="2" style={{ width: '100%', border: 'none', resize: 'vertical' }} placeholder="Details" />
                          </td>
                        </tr>
                      )}

                      {squawks.length === 0 && !isAddingSquawk ? (
                        <tr>
                          <td colSpan={isAdminOrSuperuser ? 6 : 5} style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No squawks found</td>
                        </tr>
                      ) : (
                        squawks.map((squawk, index) => {
                          const isEditing = editingSquawkId === squawk.id;
                          return (
                            <tr key={squawk.id || index} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa' }}>
                              {isAdminOrSuperuser && (
                                <td style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'center' }}>
                                  <button
                                    onClick={isEditing ? () => saveSquawk({ ...editingSquawkData, id: editingSquawkId }) : () => {
                                      if (isAddingSquawk) return;
                                      setEditingSquawkId(squawk.id);
                                      setEditingSquawkData({
                                        ...squawk,
                                        reportedDatetime: squawk.reportedDatetime || '',
                                        fixedDate: squawk.fixedDate ? squawk.fixedDate.split('T')[0] : ''
                                      });
                                    }}
                                    disabled={isAddingSquawk || (editingSquawkId && editingSquawkId !== squawk.id)}
                                    style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', opacity: (isAddingSquawk || (editingSquawkId && editingSquawkId !== squawk.id)) ? 0.5 : 1 }}
                                  >
                                    {isEditing ? '💾' : '✏️'}
                                  </button>
                                  {isEditing && (
                                    <button onClick={() => { setEditingSquawkId(null); setEditingSquawkData({}); }} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', marginLeft: '0.25rem' }}>❌</button>
                                  )}
                                </td>
                              )}
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing ? toLocalDatetimeDisplay(editingSquawkData.reportedDatetime) : toLocalDatetimeDisplay(squawk.reportedDatetime)}
                              </td>
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing ? (
                                  <input type="date" value={editingSquawkData.fixedDate || ''} onChange={(e) => setEditingSquawkData({ ...editingSquawkData, fixedDate: e.target.value })} style={{ width: '100%', border: 'none' }} />
                                ) : (squawk.fixedDate ? squawk.fixedDate.split('T')[0] : '')}
                              </td>
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing ? (
                                  <select value={editingSquawkData.status || 'New'} onChange={(e) => setEditingSquawkData({ ...editingSquawkData, status: e.target.value })} style={{ width: '100%', border: 'none', backgroundColor: 'transparent' }}>
                                    {squawkStatusOptions.map((option) => (
                                      <option key={option} value={option}>{option}</option>
                                    ))}
                                  </select>
                                ) : (squawk.status || '')}
                              </td>
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing ? (
                                  <input type="text" maxLength={200} value={editingSquawkData.issue || ''} onChange={(e) => setEditingSquawkData({ ...editingSquawkData, issue: e.target.value })} style={{ width: '100%', border: 'none' }} />
                                ) : (squawk.issue || '')}
                              </td>
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing ? (
                                  <textarea value={editingSquawkData.details || ''} onChange={(e) => setEditingSquawkData({ ...editingSquawkData, details: e.target.value })} rows="2" style={{ width: '100%', border: 'none', resize: 'vertical' }} />
                                ) : (squawk.details || '')}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'log' && (
            <div style={{ padding: '2rem', backgroundColor: '#fff2e6', border: '1px solid #ccc', borderTop: 'none', height: '400px', display: 'flex', flexDirection: 'column' }}>
              {logMessage && (
                <div style={{ padding: '0.5rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: logMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: logMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${logMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>
                  {logMessage}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div>
                    <label style={{ marginRight: '0.5rem' }}>Records per page:</label>
                    <select value={recordsPerPage} onChange={(e) => {
                      const newPerPage = parseInt(e.target.value);
                      setRecordsPerPage(newPerPage);
                      setLogPage(1);
                      if (editingId) fetchAircraftLog(editingId, 1, newPerPage);
                    }} style={{ padding: '0.25rem' }}>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  {isAdminOrSuperuser && (
                    <button onClick={handleAddNew} disabled={isAddingNew || editingLogId} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: (isAddingNew || editingLogId) ? 0.5 : 1 }}>Add New Entry</button>
                  )}
                </div>
                <div>
                  <button onClick={() => {
                    const newPage = Math.max(1, logPage - 1);
                    setLogPage(newPage);
                    if (editingId) fetchAircraftLog(editingId, newPage, recordsPerPage);
                  }} disabled={logPage <= 1} style={{ marginRight: '0.5rem', padding: '0.25rem 0.5rem' }}>Previous</button>
                  <span style={{ margin: '0 0.5rem' }}>Page {logPage}</span>
                  <button onClick={() => {
                    const newPage = logPage + 1;
                    setLogPage(newPage);
                    if (editingId) fetchAircraftLog(editingId, newPage, recordsPerPage);
                  }} disabled={aircraftLog.length < recordsPerPage} style={{ marginLeft: '0.5rem', padding: '0.25rem 0.5rem' }}>Next</button>
                </div>
              </div>
              {loadingLog ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>Loading aircraft log...</div>
              ) : (
                <div style={{ overflowX: 'auto', overflowY: 'auto', height: '360px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f0f0f0', position: 'sticky', top: 0 }}>
                        {isAdminOrSuperuser && <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', width: '40px' }}></th>}
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', minWidth: '88px' }}>Date</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', minWidth: '80px' }}>Origin</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Route</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Destination</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Hobbs Start</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Hobbs End</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Flight Time</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Tach Start</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Tach End</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Engine Time</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Pilot</th>
                        {isAdminOrSuperuser && <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Fuel Gallons</th>}
                        {isAdminOrSuperuser && <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Fuel Cost</th>}
                        {isAdminOrSuperuser && <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Remarks</th>}
                        {isAdminOrSuperuser && <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Maintenance</th>}
                        {isAdminOrSuperuser && <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Maintenance Cost</th>}
                        {isAdminOrSuperuser && <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Owner Disbursement</th>}
                        {isAdminOrSuperuser && <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Engine Reserve</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {isAddingNew && (
                        <tr style={{ backgroundColor: '#e8f5e8' }}>
                          {isAdminOrSuperuser && (
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'center' }}>
                              <button onClick={handleSaveNew} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', marginRight: '0.25rem' }}>💾</button>
                              <button onClick={handleCancelNew} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                            </td>
                          )}
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <input type="date" value={newLogData.date || ''} onChange={(e) => setNewLogData({...newLogData, date: e.target.value})} style={{ width: '100%', border: 'none' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <input type="text" value={newLogData.origin || ''} onChange={(e) => setNewLogData({...newLogData, origin: e.target.value})} style={{ width: '100%', border: 'none' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <input type="text" value={newLogData.route || ''} onChange={(e) => setNewLogData({...newLogData, route: e.target.value})} style={{ width: '100%', border: 'none' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <input type="text" value={newLogData.destination || ''} onChange={(e) => setNewLogData({...newLogData, destination: e.target.value})} style={{ width: '100%', border: 'none' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc', minWidth: '80px' }}>
                            <input type="number" step="0.1" value={newLogData.hobbsStart || ''} onChange={(e) => setNewLogData({...newLogData, hobbsStart: e.target.value})} style={{ width: '100%', border: 'none', minWidth: '70px' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc', minWidth: '80px' }}>
                            <input type="number" step="0.1" value={newLogData.hobbsEnd || ''} onChange={(e) => {
                              const hobbsEnd = e.target.value;
                              const flightTime = hobbsEnd && newLogData.hobbsStart ? (parseFloat(hobbsEnd) - parseFloat(newLogData.hobbsStart)).toFixed(1) : '';
                              setNewLogData({...newLogData, hobbsEnd, flightTime});
                            }} style={{ width: '100%', border: 'none', minWidth: '70px' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <input type="number" step="0.1" value={newLogData.flightTime || ''} onChange={(e) => setNewLogData({...newLogData, flightTime: e.target.value})} style={{ width: '100%', border: 'none' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc', minWidth: '80px' }}>
                            <input type="number" step="0.01" value={newLogData.tachStart || ''} onChange={(e) => setNewLogData({...newLogData, tachStart: e.target.value})} style={{ width: '100%', border: 'none', minWidth: '70px' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc', minWidth: '80px' }}>
                            <input type="number" step="0.01" value={newLogData.tachEnd || ''} onChange={(e) => {
                              const tachEnd = e.target.value;
                              const engineTime = tachEnd && newLogData.tachStart ? (parseFloat(tachEnd) - parseFloat(newLogData.tachStart)).toFixed(2) : '';
                              setNewLogData({...newLogData, tachEnd, engineTime});
                            }} style={{ width: '100%', border: 'none', minWidth: '70px' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <input type="number" step="0.01" value={newLogData.engineTime || ''} onChange={(e) => setNewLogData({...newLogData, engineTime: e.target.value})} style={{ width: '100%', border: 'none' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <input type="text" value={newLogData.pilot || ''} onChange={(e) => setNewLogData({...newLogData, pilot: e.target.value})} style={{ width: '100%', border: 'none' }} />
                          </td>
                          {isAdminOrSuperuser && (
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              <input type="number" step="0.1" value={newLogData.fuelGallons || ''} onChange={(e) => setNewLogData({...newLogData, fuelGallons: e.target.value})} style={{ width: '100%', border: 'none' }} />
                            </td>
                          )}
                          {isAdminOrSuperuser && (
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              <input type="number" step="0.01" value={newLogData.fuelCost || ''} onChange={(e) => setNewLogData({...newLogData, fuelCost: e.target.value})} style={{ width: '100%', border: 'none' }} />
                            </td>
                          )}
                          {isAdminOrSuperuser && (
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              <input type="text" value={newLogData.remarks || ''} onChange={(e) => setNewLogData({...newLogData, remarks: e.target.value})} style={{ width: '100%', border: 'none' }} />
                            </td>
                          )}
                          {isAdminOrSuperuser && (
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              <input type="text" value={newLogData.maintenance || ''} onChange={(e) => setNewLogData({...newLogData, maintenance: e.target.value})} style={{ width: '100%', border: 'none' }} />
                            </td>
                          )}
                          {isAdminOrSuperuser && (
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              <input type="number" step="0.01" value={newLogData.maintenanceCost || ''} onChange={(e) => setNewLogData({...newLogData, maintenanceCost: e.target.value})} style={{ width: '100%', border: 'none' }} />
                            </td>
                          )}
                          {isAdminOrSuperuser && (
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              <input type="number" step="0.01" value={newLogData.ownerDisbursement || ''} onChange={(e) => setNewLogData({...newLogData, ownerDisbursement: e.target.value})} style={{ width: '100%', border: 'none' }} />
                            </td>
                          )}
                          {isAdminOrSuperuser && (
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              <input type="number" step="0.01" value={newLogData.engineReserve || ''} onChange={(e) => setNewLogData({...newLogData, engineReserve: e.target.value})} style={{ width: '100%', border: 'none' }} />
                            </td>
                          )}
                        </tr>
                      )}
                      {aircraftLog.map((entry, index) => {
                        const isEditing = editingLogId === entry.id;
                        return (
                          <tr key={entry.id} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa' }}>
                            {isAdminOrSuperuser && (
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'center' }}>
                                <button onClick={isEditing ? handleSaveLog : () => handleEditLog(entry)} disabled={isAddingNew || (editingLogId && editingLogId !== entry.id)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', opacity: (isAddingNew || (editingLogId && editingLogId !== entry.id)) ? 0.5 : 1 }}>
                                  {isEditing ? '💾' : '✏️'}
                                </button>
                              </td>
                            )}
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              {isEditing ? <input type="date" value={editingLogData.date || ''} onChange={(e) => setEditingLogData({...editingLogData, date: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (entry.date ? (() => {
                                const dateStr = entry.date.split('T')[0];
                                const [year, month, day] = dateStr.split('-');
                                return `${month}/${day}/${year}`;
                              })() : '')}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              {isEditing ? <input type="text" value={editingLogData.origin || ''} onChange={(e) => setEditingLogData({...editingLogData, origin: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (entry.origin || '')}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              {isEditing ? <input type="text" value={editingLogData.route || ''} onChange={(e) => setEditingLogData({...editingLogData, route: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (entry.route || '')}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              {isEditing ? <input type="text" value={editingLogData.destination || ''} onChange={(e) => setEditingLogData({...editingLogData, destination: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (entry.destination || '')}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc', minWidth: '80px' }}>
                              {isEditing ? <input type="number" step="0.1" value={editingLogData.hobbsStart || ''} onChange={(e) => setEditingLogData({...editingLogData, hobbsStart: e.target.value})} style={{ width: '100%', border: 'none', minWidth: '70px' }} /> : (entry.hobbsStart || '')}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc', minWidth: '80px' }}>
                              {isEditing ? <input type="number" step="0.1" value={editingLogData.hobbsEnd || ''} onChange={(e) => {
                                const hobbsEnd = e.target.value;
                                const flightTime = hobbsEnd && editingLogData.hobbsStart ? (parseFloat(hobbsEnd) - parseFloat(editingLogData.hobbsStart)).toFixed(1) : editingLogData.flightTime;
                                setEditingLogData({...editingLogData, hobbsEnd, flightTime});
                              }} style={{ width: '100%', border: 'none', minWidth: '70px' }} /> : (entry.hobbsEnd || '')}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              {isEditing ? <input type="number" step="0.1" value={editingLogData.flightTime || ''} onChange={(e) => setEditingLogData({...editingLogData, flightTime: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (entry.flightTime || '')}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc', minWidth: '80px' }}>
                              {isEditing ? <input type="number" step="0.01" value={editingLogData.tachStart || ''} onChange={(e) => setEditingLogData({...editingLogData, tachStart: e.target.value})} style={{ width: '100%', border: 'none', minWidth: '70px' }} /> : (entry.tachStart || '')}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc', minWidth: '80px' }}>
                              {isEditing ? <input type="number" step="0.01" value={editingLogData.tachEnd || ''} onChange={(e) => {
                                const tachEnd = e.target.value;
                                const engineTime = tachEnd && editingLogData.tachStart ? (parseFloat(tachEnd) - parseFloat(editingLogData.tachStart)).toFixed(2) : editingLogData.engineTime;
                                setEditingLogData({...editingLogData, tachEnd, engineTime});
                              }} style={{ width: '100%', border: 'none', minWidth: '70px' }} /> : (entry.tachEnd || '')}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              {isEditing ? <input type="number" step="0.01" value={editingLogData.engineTime || ''} onChange={(e) => setEditingLogData({...editingLogData, engineTime: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (entry.engineTime || '')}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              {isEditing ? <input type="text" value={editingLogData.pilot || ''} onChange={(e) => setEditingLogData({...editingLogData, pilot: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (entry.pilot || '')}
                            </td>
                            {isAdminOrSuperuser && (
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing ? <input type="number" step="0.1" value={editingLogData.fuelGallons || ''} onChange={(e) => setEditingLogData({...editingLogData, fuelGallons: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (entry.fuelGallons || '')}
                              </td>
                            )}
                            {isAdminOrSuperuser && (
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing ? <input type="number" step="0.01" value={editingLogData.fuelCost || ''} onChange={(e) => setEditingLogData({...editingLogData, fuelCost: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (entry.fuelCost || '')}
                              </td>
                            )}
                            {isAdminOrSuperuser && (
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing ? <input type="text" value={editingLogData.remarks || ''} onChange={(e) => setEditingLogData({...editingLogData, remarks: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (entry.remarks || '')}
                              </td>
                            )}
                            {isAdminOrSuperuser && (
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing ? <input type="text" value={editingLogData.maintenance || ''} onChange={(e) => setEditingLogData({...editingLogData, maintenance: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (entry.maintenance || '')}
                              </td>
                            )}
                            {isAdminOrSuperuser && (
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing ? <input type="number" step="0.01" value={editingLogData.maintenanceCost || ''} onChange={(e) => setEditingLogData({...editingLogData, maintenanceCost: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (entry.maintenanceCost || '')}
                              </td>
                            )}
                            {isAdminOrSuperuser && (
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing ? <input type="number" step="0.01" value={editingLogData.ownerDisbursement || ''} onChange={(e) => setEditingLogData({...editingLogData, ownerDisbursement: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (entry.ownerDisbursement || '')}
                              </td>
                            )}
                            {isAdminOrSuperuser && (
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing ? <input type="number" step="0.01" value={editingLogData.engineReserve || ''} onChange={(e) => setEditingLogData({...editingLogData, engineReserve: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (entry.engineReserve || '')}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                      {aircraftLog.length === 0 && !loadingLog && (
                        <tr>
                          <td colSpan={isAdminOrSuperuser ? "19" : "12"} style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No log entries found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'maintenance' && (isAdminOrSuperuser || isInstructor || isOwnerOfEditingAircraft) && (
            <div style={{ padding: '2rem', backgroundColor: '#f0e6ff', border: '1px solid #ccc', borderTop: 'none', height: '400px', overflow: 'auto' }}>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
                <h4 style={{ margin: 0 }}>Maintenance Records</h4>
                <div>
                  <label style={{ marginRight: '0.5rem' }}>Start Date:</label>
                  <input 
                    type="date" 
                    value={maintenanceStartDate} 
                    onChange={(e) => setMaintenanceStartDate(e.target.value)}
                    style={{ padding: '0.25rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ marginRight: '0.5rem' }}>End Date:</label>
                  <input 
                    type="date" 
                    value={maintenanceEndDate} 
                    onChange={(e) => setMaintenanceEndDate(e.target.value)}
                    style={{ padding: '0.25rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
                <button 
                  onClick={() => editingId && fetchMaintenanceRecords(editingId)}
                  style={{ 
                    padding: '0.25rem 0.5rem', 
                    backgroundColor: '#007bff', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer' 
                  }}
                >
                  Refresh
                </button>
                {isAdminOrSuperuser && (
                  <button 
                    onClick={handleAddMaintenance}
                    disabled={isAddingMaintenance}
                    style={{ 
                      padding: '0.25rem 0.5rem', 
                      backgroundColor: '#28a745', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: 'pointer',
                      opacity: isAddingMaintenance ? 0.5 : 1
                    }}
                  >
                    Add Maintenance Record
                  </button>
                )}
              </div>
              {maintenanceMessage && (
                <div style={{ 
                  padding: '0.5rem', 
                  marginBottom: '1rem', 
                  borderRadius: '4px', 
                  backgroundColor: maintenanceMessage.includes('Error') ? '#f8d7da' : '#d4edda',
                  color: maintenanceMessage.includes('Error') ? '#721c24' : '#155724',
                  border: `1px solid ${maintenanceMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`
                }}>
                  {maintenanceMessage}
                </div>
              )}
              {loadingMaintenance ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>Loading maintenance records...</div>
              ) : (
                <div style={{ overflow: 'auto', height: '280px', width: '150%' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f0f0f0', position: 'sticky', top: 0 }}>
                        {isAdminOrSuperuser && <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', width: '60px' }}>Actions</th>}
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Date</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Airport</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Vendor</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Category</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Type</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Hobbs</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Tach</th>
                        {isAdminOrSuperuser && <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Cost</th>}
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Description</th>
                        {isAdminOrSuperuser && <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Document</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {isAddingMaintenance && isAdminOrSuperuser && (
                        <tr style={{ backgroundColor: '#e8f5e8' }}>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'center' }}>
                            <button onClick={handleSaveMaintenance} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', marginRight: '0.25rem' }}>💾</button>
                            <button onClick={handleCancelMaintenance} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <input type="date" value={newMaintenanceData.maintenanceDate || ''} onChange={(e) => setNewMaintenanceData({...newMaintenanceData, maintenanceDate: e.target.value})} style={{ width: '100%', border: 'none' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <input type="text" value={newMaintenanceData.airport || ''} onChange={(e) => setNewMaintenanceData({...newMaintenanceData, airport: e.target.value})} style={{ width: '100%', border: 'none' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <input type="text" value={newMaintenanceData.vendor || ''} onChange={(e) => setNewMaintenanceData({...newMaintenanceData, vendor: e.target.value})} style={{ width: '100%', border: 'none' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <select value={newMaintenanceData.category || ''} onChange={(e) => setNewMaintenanceData({...newMaintenanceData, category: e.target.value})} style={{ width: '100%', border: 'none' }}>
                              <option value="">Select category...</option>
                              <option value="oil change">oil change</option>
                              <option value="annual">annual</option>
                              <option value="100-hour">100-hour</option>
                            </select>
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <select value={newMaintenanceData.type || ''} onChange={(e) => setNewMaintenanceData({...newMaintenanceData, type: e.target.value})} style={{ width: '100%', border: 'none' }}>
                              <option value="">Select type...</option>
                              <option value="Scheduled maintenance">Scheduled maintenance</option>
                              <option value="Upgrade">Upgrade</option>
                              <option value="Repair">Repair</option>
                              <option value="Inspection">Inspection</option>
                            </select>
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <input type="number" step="0.1" value={newMaintenanceData.hobbs || ''} onChange={(e) => setNewMaintenanceData({...newMaintenanceData, hobbs: e.target.value})} style={{ width: '100%', border: 'none' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <input type="number" step="0.01" value={newMaintenanceData.tach || ''} onChange={(e) => setNewMaintenanceData({...newMaintenanceData, tach: e.target.value})} style={{ width: '100%', border: 'none' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <input type="number" step="0.01" value={newMaintenanceData.cost || ''} onChange={(e) => setNewMaintenanceData({...newMaintenanceData, cost: e.target.value})} style={{ width: '100%', border: 'none' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <input type="text" value={newMaintenanceData.description || ''} onChange={(e) => setNewMaintenanceData({...newMaintenanceData, description: e.target.value})} style={{ width: '100%', border: 'none' }} />
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setMaintenanceRecordFile(setNewMaintenanceData, e.target.files[0])} style={{ width: '100%', border: 'none', fontSize: '12px', color: 'transparent' }} />
                            {newMaintenanceData.maintenanceRecordFile && <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>Selected: {newMaintenanceData.maintenanceRecordFile.name}</div>}
                          </td>
                        </tr>
                      )}
                      {maintenanceRecords.length === 0 && !isAddingMaintenance ? (
                        <tr>
                          <td colSpan={isAdminOrSuperuser ? "11" : "8"} style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No maintenance records found</td>
                        </tr>
                      ) : (
                        maintenanceRecords.map((record, index) => {
                          const isEditing = editingMaintenanceId === record.id;
                          return (
                            <tr key={record.id} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa' }}>
                              {isAdminOrSuperuser && (
                                <td style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'center' }}>
                                  <button 
                                    onClick={isEditing ? handleSaveEditMaintenance : () => handleEditMaintenance(record)} 
                                    disabled={isAddingMaintenance || (editingMaintenanceId && editingMaintenanceId !== record.id)}
                                    style={{ 
                                      backgroundColor: 'transparent', 
                                      border: 'none', 
                                      cursor: 'pointer', 
                                      fontSize: '14px',
                                      opacity: (isAddingMaintenance || (editingMaintenanceId && editingMaintenanceId !== record.id)) ? 0.5 : 1
                                    }}
                                  >
                                    {isEditing ? '💾' : '✏️'}
                                  </button>
                                  {isEditing && (
                                    <button 
                                      onClick={() => {
                                        setEditingMaintenanceId(null);
                                        setEditingMaintenanceData({});
                                      }} 
                                      style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', marginLeft: '0.25rem' }}
                                    >
                                      ❌
                                    </button>
                                  )}
                                  {!isEditing && (
                                    <button 
                                      onClick={() => handleDeleteMaintenance(record)}
                                      disabled={isAddingMaintenance || editingMaintenanceId}
                                      style={{ 
                                        backgroundColor: 'transparent', 
                                        border: 'none', 
                                        cursor: 'pointer', 
                                        fontSize: '14px', 
                                        marginLeft: '0.25rem',
                                        opacity: (isAddingMaintenance || editingMaintenanceId) ? 0.5 : 1
                                      }}
                                    >
                                      🗑️
                                    </button>
                                  )}
                                </td>
                              )}
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing && isAdminOrSuperuser ? 
                                  <input type="date" value={editingMaintenanceData.maintenanceDate || ''} onChange={(e) => setEditingMaintenanceData({...editingMaintenanceData, maintenanceDate: e.target.value})} style={{ width: '100%', border: 'none' }} /> :
                                  (record.maintenanceDate ? record.maintenanceDate.split('T')[0] : '')
                                }
                              </td>
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing && isAdminOrSuperuser ? 
                                  <input type="text" value={editingMaintenanceData.airport || ''} onChange={(e) => setEditingMaintenanceData({...editingMaintenanceData, airport: e.target.value})} style={{ width: '100%', border: 'none' }} /> :
                                  (record.airport || '')
                                }
                              </td>
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing && isAdminOrSuperuser ? 
                                  <input type="text" value={editingMaintenanceData.vendor || ''} onChange={(e) => setEditingMaintenanceData({...editingMaintenanceData, vendor: e.target.value})} style={{ width: '100%', border: 'none' }} /> :
                                  (record.vendor || '')
                                }
                              </td>
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing && isAdminOrSuperuser ? 
                                  <select value={editingMaintenanceData.category || ''} onChange={(e) => setEditingMaintenanceData({...editingMaintenanceData, category: e.target.value})} style={{ width: '100%', border: 'none' }}>
                                    <option value="">Select category...</option>
                                    <option value="oil change">oil change</option>
                                    <option value="annual">annual</option>
                                    <option value="100-hour">100-hour</option>
                                  </select> :
                                  (record.category || '')
                                }
                              </td>
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing && isAdminOrSuperuser ? 
                                  <select value={editingMaintenanceData.type || ''} onChange={(e) => setEditingMaintenanceData({...editingMaintenanceData, type: e.target.value})} style={{ width: '100%', border: 'none' }}>
                                    <option value="">Select type...</option>
                                    <option value="Scheduled maintenance">Scheduled maintenance</option>
                                    <option value="Upgrade">Upgrade</option>
                                    <option value="Repair">Repair</option>
                                    <option value="Inspection">Inspection</option>
                                  </select> :
                                  (record.type || '')
                                }
                              </td>
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing && isAdminOrSuperuser ? 
                                  <input type="number" step="0.1" value={editingMaintenanceData.hobbs || ''} onChange={(e) => setEditingMaintenanceData({...editingMaintenanceData, hobbs: e.target.value})} style={{ width: '100%', border: 'none' }} /> :
                                  (record.hobbs || '')
                                }
                              </td>
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing && isAdminOrSuperuser ? 
                                  <input type="number" step="0.01" value={editingMaintenanceData.tach || ''} onChange={(e) => setEditingMaintenanceData({...editingMaintenanceData, tach: e.target.value})} style={{ width: '100%', border: 'none' }} /> :
                                  (record.tach || '')
                                }
                              </td>
                              {isAdminOrSuperuser && <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing ?
                                  <input type="number" step="0.01" value={editingMaintenanceData.cost || ''} onChange={(e) => setEditingMaintenanceData({...editingMaintenanceData, cost: e.target.value})} style={{ width: '100%', border: 'none' }} /> :
                                  (record.cost ? `$${record.cost}` : '')
                                }
                              </td>}
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {isEditing && isAdminOrSuperuser ? 
                                  <input type="text" value={editingMaintenanceData.description || ''} onChange={(e) => setEditingMaintenanceData({...editingMaintenanceData, description: e.target.value})} style={{ width: '100%', border: 'none' }} /> :
                                  (record.description || '')
                                }
                              </td>
                              {isAdminOrSuperuser && <td style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'center' }}>
                                {isEditing ? (
                                  <div>
                                    <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setMaintenanceRecordFile(setEditingMaintenanceData, e.target.files[0])} style={{ width: '100%', border: 'none', fontSize: '12px', color: 'transparent' }} />
                                    {editingMaintenanceData.maintenanceRecordFile && <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>Selected: {editingMaintenanceData.maintenanceRecordFile.name}</div>}
                                    {!editingMaintenanceData.maintenanceRecordFile && record.maintenanceRecordImage && <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>Current file attached</div>}
                                  </div>
                                ) : (
                                  (record.maintenanceRecordImage || record.isAttachment) ? (
                                    <span
                                      onClick={() => openMaintenanceRecordDocument(record)}
                                      style={{ cursor: 'pointer', fontSize: '16px' }}
                                      title="Click to view maintenance document"
                                    >
                                      📎
                                    </span>
                                  ) : ''
                                )}
                              </td>}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'ownerExpenses' && (isAdminOrSuperuser || isOwnerOfEditingAircraft) && (
            <div style={{ padding: '2rem', backgroundColor: '#e6fff0', border: '1px solid #ccc', borderTop: 'none', height: '400px', overflow: 'auto' }}>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
                <h4 style={{ margin: 0 }}>Owner Expenses</h4>
                <button onClick={() => editingId && fetchOwnerExpenses(editingId)} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Refresh</button>
                {isAdminOrSuperuser && (
                  <button onClick={() => { setIsAddingOwnerExpense(true); const ca = (Array.isArray(aircraft) ? aircraft : []).find(a => a.id === editingId); setNewOwnerExpenseData({ aircraftId: editingId, ownerId: ca?.ownerId || '', date: new Date().toISOString().split('T')[0] }); }} disabled={isAddingOwnerExpense} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: isAddingOwnerExpense ? 0.5 : 1 }}>Add Expense</button>
                )}
              </div>
              {ownerExpenseMessage && (
                <div style={{ padding: '0.5rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: ownerExpenseMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: ownerExpenseMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${ownerExpenseMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>
                  {ownerExpenseMessage}
                </div>
              )}
              {loadingOwnerExpenses ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>Loading owner expenses...</div>
              ) : (
                <div style={{ overflow: 'auto', height: '280px', width: '150%' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f0f0f0', position: 'sticky', top: 0 }}>
                        {isAdminOrSuperuser && <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', width: '80px' }}>Actions</th>}
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', width: '109px' }}>Date</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Expense</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left', width: '90px' }}>Type</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Description</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isAddingOwnerExpense && isAdminOrSuperuser && (
                        <tr style={{ backgroundColor: '#e8f5e8' }}>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'center' }}>
                            <button onClick={() => saveOwnerExpense(true)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', marginRight: '0.25rem' }}>💾</button>
                            <button onClick={() => { setIsAddingOwnerExpense(false); setNewOwnerExpenseData({}); }} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}><input type="date" value={newOwnerExpenseData.date || ''} onChange={(e) => setNewOwnerExpenseData({...newOwnerExpenseData, date: e.target.value})} style={{ width: '100%', border: 'none' }} /></td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}><input type="text" value={newOwnerExpenseData.expense || ''} onChange={(e) => setNewOwnerExpenseData({...newOwnerExpenseData, expense: e.target.value})} style={{ width: '100%', border: 'none' }} /></td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                            <select value={newOwnerExpenseData.type || ''} onChange={(e) => setNewOwnerExpenseData({...newOwnerExpenseData, type: e.target.value})} style={{ width: '100%', border: 'none' }}>
                              <option value="">Select...</option>
                              <option value="maintenance">maintenance</option>
                              <option value="repair">repair</option>
                              <option value="upgrade">upgrade</option>
                              <option value="other">other</option>
                            </select>
                          </td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}><input type="text" value={newOwnerExpenseData.description || ''} onChange={(e) => setNewOwnerExpenseData({...newOwnerExpenseData, description: e.target.value})} style={{ width: '100%', border: 'none' }} /></td>
                          <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}><input type="number" step="0.01" value={newOwnerExpenseData.amount || ''} onChange={(e) => setNewOwnerExpenseData({...newOwnerExpenseData, amount: e.target.value})} style={{ width: '100%', border: 'none' }} /></td>
                        </tr>
                      )}
                      {ownerExpenses.length === 0 && !isAddingOwnerExpense ? (
                        <tr><td colSpan={isAdminOrSuperuser ? '6' : '5'} style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>No owner expenses found</td></tr>
                      ) : (
                        ownerExpenses.map((expense, index) => {
                          const isEditing = editingOwnerExpenseId === expense.id;
                          return (
                            <tr key={expense.id} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa' }}>
                              {isAdminOrSuperuser && (
                                <td style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'center' }}>
                                  <button onClick={isEditing ? () => saveOwnerExpense(false) : () => { setEditingOwnerExpenseId(expense.id); setEditingOwnerExpenseData({...expense, date: expense.date ? expense.date.split('T')[0] : ''}); }} disabled={isAddingOwnerExpense || (editingOwnerExpenseId && editingOwnerExpenseId !== expense.id)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', opacity: (isAddingOwnerExpense || (editingOwnerExpenseId && editingOwnerExpenseId !== expense.id)) ? 0.5 : 1 }}>{isEditing ? '💾' : '✏️'}</button>
                                  {isEditing ? (
                                    <button onClick={() => { setEditingOwnerExpenseId(null); setEditingOwnerExpenseData({}); }} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', marginLeft: '0.25rem' }}>❌</button>
                                  ) : (
                                    <button onClick={() => { setDeleteOwnerExpenseRecord(expense); setShowDeleteOwnerExpenseModal(true); }} disabled={isAddingOwnerExpense || editingOwnerExpenseId} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', marginLeft: '0.25rem', opacity: (isAddingOwnerExpense || editingOwnerExpenseId) ? 0.5 : 1 }}>🗑️</button>
                                  )}
                                </td>
                              )}
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>{isEditing ? <input type="date" value={editingOwnerExpenseData.date || ''} onChange={(e) => setEditingOwnerExpenseData({...editingOwnerExpenseData, date: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (expense.date ? expense.date.split('T')[0] : '')}</td>
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>{isEditing ? <input type="text" value={editingOwnerExpenseData.expense || ''} onChange={(e) => setEditingOwnerExpenseData({...editingOwnerExpenseData, expense: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (expense.expense || '')}</td>
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>{isEditing ? <select value={editingOwnerExpenseData.type || ''} onChange={(e) => setEditingOwnerExpenseData({...editingOwnerExpenseData, type: e.target.value})} style={{ width: '100%', border: 'none' }}><option value="">Select...</option><option value="maintenance">maintenance</option><option value="repair">repair</option><option value="upgrade">upgrade</option><option value="other">other</option></select> : (expense.type || '')}</td>
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>{isEditing ? <input type="text" value={editingOwnerExpenseData.description || ''} onChange={(e) => setEditingOwnerExpenseData({...editingOwnerExpenseData, description: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (expense.description || '')}</td>
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>{isEditing ? <input type="number" step="0.01" value={editingOwnerExpenseData.amount || ''} onChange={(e) => setEditingOwnerExpenseData({...editingOwnerExpenseData, amount: e.target.value})} style={{ width: '100%', border: 'none' }} /> : (expense.amount ? `$${expense.amount}` : '')}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'ownerStatements' && (isAdminOrSuperuser || isOwnerOfEditingAircraft) && (
            <div style={{ padding: '2rem', backgroundColor: '#f0f0ff', border: '1px solid #ccc', borderTop: 'none', height: '400px', overflow: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4 style={{ margin: 0 }}>Owner Statements</h4>
                {isAdminOrSuperuser && (
                  <button onClick={addNewOwnerStatement} style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Add New Statement</button>
                )}
              </div>
              {ownerStatementMessage && (
                <div style={{ padding: '0.5rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: ownerStatementMessage.includes('Error') ? '#f8d7da' : '#d4edda', color: ownerStatementMessage.includes('Error') ? '#721c24' : '#155724', border: `1px solid ${ownerStatementMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}` }}>
                  {ownerStatementMessage}
                </div>
              )}
              {ownerStatements.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#666' }}>No owner statements found</div>
              ) : (
                <div style={{ overflow: 'auto', height: '280px', width: '150%' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f0f0f0', position: 'sticky', top: 0 }}>
                        {isAdminOrSuperuser && <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Actions</th>}
                        {isAdminOrSuperuser && <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Owner</th>}
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Start Date</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>End Date</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Owner Disbursement</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Engine Reserve</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Upgrade Total</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Owner Expenses</th>
                        <th style={{ padding: '0.5rem', border: '1px solid #ccc', textAlign: 'left' }}>Net Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ownerStatements.map((statement, index) => {
                        const isEditing = editingOwnerStatementId === statement.id;
                        return (
                          <tr key={statement.id} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa', cursor: !isEditing ? 'pointer' : 'default' }} onClick={() => !isEditing && handleOwnerStatementClick(statement)}>
                            {isAdminOrSuperuser && (
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }} onClick={(e) => e.stopPropagation()}>
                                <div style={{ display: 'flex', gap: '0.25rem' }}>
                                  {isEditing && isAdminOrSuperuser ? (
                                    <>
                                      <button onClick={() => saveOwnerStatement(statement)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>💾</button>
                                      <button onClick={() => setEditingOwnerStatementId(null)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                                    </>
                                  ) : isAdminOrSuperuser ? (
                                    <>
                                      <button onClick={() => setEditingOwnerStatementId(statement.id)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>✏️</button>
                                      {!statement.id?.toString().startsWith('temp_') && (
                                        <button onClick={(e) => { e.stopPropagation(); openOwnerStatementPdf(statement.id); }} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }} title="Download PDF">📄</button>
                                      )}
                                    </>
                                  ) : null}
                                </div>
                              </td>
                            )}
                            {isAdminOrSuperuser && (
                              <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                {(() => {
                                  const currentAircraft = (Array.isArray(aircraft) ? aircraft : []).find(a => a.id === editingId);
                                  const owner = (Array.isArray(people) ? people : []).find(p => p.id === (statement.ownerId || currentAircraft?.ownerId));
                                  return owner ? `${owner.firstName} ${owner.lastName}` : '';
                                })()
                                }
                              </td>
                            )}
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              {isEditing && isAdminOrSuperuser ? <input type="date" value={statement.startDate} onChange={(e) => updateOwnerStatementField(statement.id, 'startDate', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (statement.startDate ? statement.startDate.split('T')[0] : '')}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              {isEditing && isAdminOrSuperuser ? <input type="date" value={statement.endDate} onChange={(e) => updateOwnerStatementField(statement.id, 'endDate', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (statement.endDate ? statement.endDate.split('T')[0] : '')}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              {isEditing && isAdminOrSuperuser ? <input type="number" step="0.01" value={statement.ownerDisbursementTotal} onChange={(e) => updateOwnerStatementField(statement.id, 'ownerDisbursementTotal', e.target.value)} style={{ width: '100%', border: 'none' }} /> : `$${statement.ownerDisbursementTotal || 0}`}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              {isEditing && isAdminOrSuperuser ? <input type="number" step="0.01" value={statement.engineReserveTotal} onChange={(e) => updateOwnerStatementField(statement.id, 'engineReserveTotal', e.target.value)} style={{ width: '100%', border: 'none' }} /> : `$${statement.engineReserveTotal || 0}`}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              {isEditing && isAdminOrSuperuser ? <input type="number" step="0.01" value={statement.upgradeTotal} onChange={(e) => updateOwnerStatementField(statement.id, 'upgradeTotal', e.target.value)} style={{ width: '100%', border: 'none' }} /> : `$${statement.upgradeTotal || 0}`}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              ${statement.expenseTotal || 0}
                            </td>
                            <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                              ${statement.netTotal || 0}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'blockouts' && (isAdminOrSuperuser || isOwnerOfEditingAircraft) && (
            <div style={{ padding: '1rem', backgroundColor: '#fff5f5', border: '1px solid #ccc', borderTop: 'none', height: '400px', overflow: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h4 style={{ margin: 0 }}>Scheduled Blockouts</h4>
                {isAdminOrSuperuser && (
                  <button
                    onClick={() => { setIsAddingBlockout(true); setNewBlockoutData({ startDatetime: '', endDatetime: '', reason: '' }); }}
                    disabled={isAddingBlockout}
                    style={{ padding: '0.4rem 1rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
                  >+ Add Blockout</button>
                )}
              </div>
              {blockoutMessage && (
                <div style={{ padding: '0.5rem', marginBottom: '0.75rem', borderRadius: '4px', backgroundColor: blockoutMessage.startsWith('Error') ? '#f8d7da' : '#d4edda', color: blockoutMessage.startsWith('Error') ? '#721c24' : '#155724', border: `1px solid ${blockoutMessage.startsWith('Error') ? '#f5c6cb' : '#c3e6cb'}`, fontSize: '0.85rem' }}>
                  {blockoutMessage}
                </div>
              )}
              {isAddingBlockout && isAdminOrSuperuser && (
                <div style={{ backgroundColor: '#fff', border: '1px solid #f5c6cb', borderRadius: '6px', padding: '1rem', marginBottom: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.25rem' }}>Start Date/Time *</label>
                      <input type="datetime-local" value={newBlockoutData.startDatetime}
                        onChange={e => setNewBlockoutData({ ...newBlockoutData, startDatetime: e.target.value })}
                        style={{ width: '100%', padding: '0.4rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.25rem' }}>End Date/Time *</label>
                      <input type="datetime-local" value={newBlockoutData.endDatetime}
                        onChange={e => setNewBlockoutData({ ...newBlockoutData, endDatetime: e.target.value })}
                        style={{ width: '100%', padding: '0.4rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem' }} />
                    </div>
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.25rem' }}>Reason</label>
                    <input type="text" value={newBlockoutData.reason} placeholder="e.g. Annual inspection"
                      onChange={e => setNewBlockoutData({ ...newBlockoutData, reason: e.target.value })}
                      style={{ width: '100%', padding: '0.4rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => saveBlockout(newBlockoutData)}
                      style={{ padding: '0.4rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}>Save</button>
                    <button onClick={() => { setIsAddingBlockout(false); setNewBlockoutData({ startDatetime: '', endDatetime: '', reason: '' }); }}
                      style={{ padding: '0.4rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}>Cancel</button>
                  </div>
                </div>
              )}
              {loadingBlockouts ? (
                <div style={{ textAlign: 'center', padding: '1rem', color: '#6c757d' }}>Loading blockouts...</div>
              ) : blockouts.length === 0 && !isAddingBlockout ? (
                <div style={{ textAlign: 'center', padding: '1rem', color: '#6c757d', fontSize: '0.9rem' }}>No blockouts scheduled for this aircraft.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.5rem', textAlign: 'left' }}>Start</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.5rem', textAlign: 'left' }}>End</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.5rem', textAlign: 'left' }}>Reason</th>
                      {isAdminOrSuperuser && <th style={{ border: '1px solid #dee2e6', padding: '0.5rem', width: '130px' }}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {blockouts.map(b => (isAdminOrSuperuser && editingBlockoutId === b.id) ? (
                      <tr key={b.id} style={{ backgroundColor: '#fff5f5' }}>
                        <td style={{ border: '1px solid #dee2e6', padding: '0.4rem' }}>
                          <input type="datetime-local" value={editingBlockoutData.startDatetime || ''}
                            onChange={e => setEditingBlockoutData({ ...editingBlockoutData, startDatetime: e.target.value })}
                            style={{ width: '100%', padding: '0.25rem', border: '1px solid #ccc', borderRadius: '3px', fontSize: '0.8rem' }} />
                        </td>
                        <td style={{ border: '1px solid #dee2e6', padding: '0.4rem' }}>
                          <input type="datetime-local" value={editingBlockoutData.endDatetime || ''}
                            onChange={e => setEditingBlockoutData({ ...editingBlockoutData, endDatetime: e.target.value })}
                            style={{ width: '100%', padding: '0.25rem', border: '1px solid #ccc', borderRadius: '3px', fontSize: '0.8rem' }} />
                        </td>
                        <td style={{ border: '1px solid #dee2e6', padding: '0.4rem' }}>
                          <input type="text" value={editingBlockoutData.reason || ''}
                            onChange={e => setEditingBlockoutData({ ...editingBlockoutData, reason: e.target.value })}
                            style={{ width: '100%', padding: '0.25rem', border: '1px solid #ccc', borderRadius: '3px', fontSize: '0.8rem' }} />
                        </td>
                        <td style={{ border: '1px solid #dee2e6', padding: '0.4rem', textAlign: 'center' }}>
                          <button onClick={() => saveBlockout({ ...editingBlockoutData, id: b.id })}
                            style={{ padding: '0.2rem 0.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '0.75rem', marginRight: '4px' }}>Save</button>
                          <button onClick={() => setEditingBlockoutId(null)}
                            style={{ padding: '0.2rem 0.5rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '0.75rem' }}>Cancel</button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={b.id}>
                        <td style={{ border: '1px solid #dee2e6', padding: '0.5rem' }}>{b.startDatetime ? new Date(b.startDatetime).toLocaleString() : ''}</td>
                        <td style={{ border: '1px solid #dee2e6', padding: '0.5rem' }}>{b.endDatetime ? new Date(b.endDatetime).toLocaleString() : ''}</td>
                        <td style={{ border: '1px solid #dee2e6', padding: '0.5rem' }}>{b.reason || ''}</td>
                        {isAdminOrSuperuser && (
                          <td style={{ border: '1px solid #dee2e6', padding: '0.5rem', textAlign: 'center' }}>
                            <ActionIconButton action="edit" label="Edit aircraft blockout" onClick={() => {
                              const toLocalInput = (utcStr) => {
                                if (!utcStr) return '';
                                const d = new Date(utcStr);
                                const pad = n => String(n).padStart(2, '0');
                                return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                              };
                              setEditingBlockoutId(b.id);
                              setEditingBlockoutData({ startDatetime: toLocalInput(b.startDatetime), endDatetime: toLocalInput(b.endDatetime), reason: b.reason || '' });
                            }}
                              style={{ marginRight: '4px' }} />
                            <ActionIconButton action="delete" label="Delete aircraft blockout" onClick={() => deleteBlockout(b.id)} />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Owner Statement Details Modal */}
      {showOwnerStatementDetailsDialog && selectedOwnerStatement && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2500 }}>
          <div style={{ backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '900px', height: '600px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', backgroundColor: '#e6f7ff', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
              <h3 style={{ textAlign: 'center', margin: '0', padding: '1rem' }}>Owner Statement Details ({selectedOwnerStatement.startDate?.split('T')[0]} - {selectedOwnerStatement.endDate?.split('T')[0]})</h3>
              <div style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button onClick={() => openOwnerStatementPdf(selectedOwnerStatement.id)} style={{ padding: '0.25rem 0.75rem', backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}>🖨 Print / PDF</button>
                <button onClick={() => setShowOwnerStatementDetailsDialog(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
              </div>
            </div>
            
            <div style={{ display: 'flex', backgroundColor: '#f8f9fa', paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '1rem' }}>
              <button type="button" onClick={() => setOwnerStatementDetailsActiveTab('aircraftLog')} style={{ 
                padding: '0.5rem 0.75rem', 
                backgroundColor: ownerStatementDetailsActiveTab === 'aircraftLog' ? '#ffe6f0' : '#e9ecef', 
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: ownerStatementDetailsActiveTab === 'aircraftLog' ? '1px solid #ffe6f0' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: ownerStatementDetailsActiveTab === 'aircraftLog' ? 'bold' : 'normal',
                marginRight: '2px',
                fontSize: '0.8rem'
              }}>Aircraft Log</button>
              <button type="button" onClick={() => setOwnerStatementDetailsActiveTab('maintenance')} style={{ 
                padding: '0.5rem 0.75rem', 
                backgroundColor: ownerStatementDetailsActiveTab === 'maintenance' ? '#fff0e6' : '#e9ecef', 
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: ownerStatementDetailsActiveTab === 'maintenance' ? '1px solid #fff0e6' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: ownerStatementDetailsActiveTab === 'maintenance' ? 'bold' : 'normal',
                fontSize: '0.8rem',
                marginRight: '2px'
              }}>Maintenance</button>
              <button type="button" onClick={() => setOwnerStatementDetailsActiveTab('ownerExpenses')} style={{ 
                padding: '0.5rem 0.75rem', 
                backgroundColor: ownerStatementDetailsActiveTab === 'ownerExpenses' ? '#e6fff0' : '#e9ecef', 
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: ownerStatementDetailsActiveTab === 'ownerExpenses' ? '1px solid #e6fff0' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: ownerStatementDetailsActiveTab === 'ownerExpenses' ? 'bold' : 'normal',
                fontSize: '0.8rem'
              }}>Owner Expenses</button>
            </div>
            
            <div style={{ height: '450px', overflow: 'auto', backgroundColor: ownerStatementDetailsActiveTab === 'aircraftLog' ? '#ffe6f0' : ownerStatementDetailsActiveTab === 'maintenance' ? '#fff0e6' : '#e6fff0', border: '1px solid #ccc', borderTop: 'none', padding: '2rem' }}>
              {ownerStatementDetailsActiveTab === 'aircraftLog' && (
                <div>
                  <h4>Aircraft Log Records (Count: {ownerStatementAircraftLog.length})</h4>
                  <div style={{ overflow: 'auto', maxHeight: '350px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Origin</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Route</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Destination</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Pilot</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Flight Time</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Owner Disbursement</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Engine Reserve</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ownerStatementAircraftLog.map((log, index) => (
                          <tr key={log.id || index}>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {log.date ? log.date.split('T')[0] : ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {log.origin || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {log.route || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {log.destination || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {log.pilot || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {log.flightTime || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              ${log.ownerDisbursement || 0}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              ${log.engineReserve || 0}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {ownerStatementDetailsActiveTab === 'maintenance' && (
                <div>
                  <h4>Maintenance Records (Count: {ownerStatementMaintenance.length})</h4>
                  <div style={{ overflow: 'auto', maxHeight: '350px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Airport</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Type</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Vendor</th>
                          {isAdminOrSuperuser && <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Cost</th>}
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ownerStatementMaintenance.map((maintenance, index) => (
                          <tr key={maintenance.id || index}>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {maintenance.maintenanceDate ? maintenance.maintenanceDate.split('T')[0] : ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {maintenance.airport || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {maintenance.type || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {maintenance.vendor || ''}
                            </td>
                            {isAdminOrSuperuser && <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              ${maintenance.cost || 0}
                            </td>}
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {maintenance.description || ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {ownerStatementDetailsActiveTab === 'ownerExpenses' && (
                <div>
                  <h4>Owner Expenses (Count: {ownerStatementOwnerExpenses.length})</h4>
                  <div style={{ overflow: 'auto', maxHeight: '350px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Expense</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Type</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Description</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ownerStatementOwnerExpenses.length === 0 ? (
                          <tr><td colSpan="5" style={{ border: '1px solid #ddd', padding: '1rem', textAlign: 'center', color: '#666' }}>No owner expenses found</td></tr>
                        ) : (
                          ownerStatementOwnerExpenses.map((expense, index) => (
                            <tr key={expense.id || index}>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{expense.date ? expense.date.split('T')[0] : ''}</td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{expense.expense || ''}</td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{expense.type || ''}</td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{expense.description || ''}</td>
                              <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>{expense.amount ? `$${expense.amount}` : ''}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              <button onClick={() => setShowOwnerStatementDetailsDialog(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>Confirm Delete</h3>
            <p style={{ margin: '0 0 2rem 0', textAlign: 'center' }}>Are you sure you want to delete the aircraft {deleteAircraftData?.displayName}?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button onClick={confirmDelete} style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}>Yes, Delete</button>
              <button onClick={() => setShowDeleteModal(false)} style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteOwnerExpenseModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>Confirm Delete</h3>
            <p style={{ margin: '0 0 2rem 0', textAlign: 'center' }}>Are you sure you want to delete this owner expense?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button onClick={confirmDeleteOwnerExpense} style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}>Yes, Delete</button>
              <button onClick={() => { setShowDeleteOwnerExpenseModal(false); setDeleteOwnerExpenseRecord(null); }} style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showOwnerStatementPdfModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
          <div style={{ backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '90vw', height: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderBottom: '1px solid #ccc', backgroundColor: '#e6f3ff', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
              <h3 style={{ margin: 0 }}>Owner Statement PDF</h3>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <a href={ownerStatementPdfUrl} download="owner-statement.pdf" style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', textDecoration: 'none', fontSize: '0.9rem' }}>Download</a>
                <button onClick={() => { setShowOwnerStatementPdfModal(false); URL.revokeObjectURL(ownerStatementPdfUrl); setOwnerStatementPdfUrl(''); }} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', padding: '0.25rem' }}>✕</button>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <iframe src={ownerStatementPdfUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="Owner Statement PDF" />
            </div>
          </div>
        </div>
      )}

      {showDeleteMaintenanceModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>Confirm Delete</h3>
            <p style={{ margin: '0 0 2rem 0', textAlign: 'center' }}>Are you sure you want to delete this maintenance record?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button onClick={confirmDeleteMaintenance} style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}>Yes, Delete</button>
              <button onClick={() => setShowDeleteMaintenanceModal(false)} style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {aircraftImageModalIndex !== null && aircraftImages[aircraftImageModalIndex] && (
        <div onClick={() => setAircraftImageModalIndex(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, cursor: 'pointer' }}>
          <div onClick={(event) => event.stopPropagation()} style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '8px', maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }}>
            <button type="button" onClick={() => setAircraftImageModalIndex(null)} style={{ position: 'absolute', top: '0.25rem', right: '0.25rem', backgroundColor: 'white', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', fontSize: '18px', zIndex: 2 }}>×</button>
            {aircraftImageModalIndex > 0 && <button type="button" onClick={() => setAircraftImageModalIndex(aircraftImageModalIndex - 1)} aria-label="Previous image" style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', width: '42px', height: '42px', borderRadius: '50%', border: 'none', backgroundColor: 'rgba(0,0,0,0.65)', color: 'white', fontSize: '24px', cursor: 'pointer', zIndex: 2 }}>‹</button>}
            <img src={aircraftImages[aircraftImageModalIndex].imageUrl} alt={`Aircraft ${aircraftImageModalIndex + 1}`} style={{ maxWidth: '85vw', maxHeight: '80vh', objectFit: 'contain', display: 'block' }} />
            {aircraftImageModalIndex < aircraftImages.length - 1 && <button type="button" onClick={() => setAircraftImageModalIndex(aircraftImageModalIndex + 1)} aria-label="Next image" style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', width: '42px', height: '42px', borderRadius: '50%', border: 'none', backgroundColor: 'rgba(0,0,0,0.65)', color: 'white', fontSize: '24px', cursor: 'pointer', zIndex: 2 }}>›</button>}
            <div style={{ textAlign: 'center', marginTop: '0.5rem', color: '#495057', fontSize: '0.85rem' }}>{aircraftImageModalIndex + 1} of {aircraftImages.length}</div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Document</h3>
              <button onClick={closeReceiptModal} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', padding: '0.25rem' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', textAlign: 'center', width: '100%', height: '100%' }}>
              {isLoadingReceipt ? (
                <div style={{ padding: '2rem', color: '#666' }}>Loading document...</div>
              ) : receiptModalMessage ? (
                <div style={{ padding: '2rem', color: '#721c24' }}>{receiptModalMessage}</div>
              ) : selectedReceiptType === 'pdf' ? (
                <iframe src={selectedReceiptImage} style={{ width: '85vw', height: '75vh', border: 'none' }} title="Document PDF" />
              ) : (
                <img src={selectedReceiptImage} alt="Document" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Aircraft;